import React, { useMemo, useState } from "react";
import SearchableSelectField from "../../../../common/dashboard/SearchableSelectField";
import { Checkbox, Radio, Switch } from "@mui/material";
import { inlineChoiceControlSx } from "../../../../common/dashboard/formStyles";
import { ActionSuccessDialog } from "../../../../common/dashboard/ActionSuccessDialog";
import { ConfirmDialog } from "../../../../common/dashboard/ConfirmDialog";
import {
  applyCmpOrganizationNoun,
  getCmpBoardWording,
} from "../../../../../utils/cmpOrganizationNoun";
import { useCmpOrganizationNoun } from "../../../../../contexts/CmpOrganizationNounContext";
import {
  DecimalNumberInput,
  MoneyInput,
  SectionSaveReset,
  SectionSaveResetProps,
  TextInput,
  WholeNumberInput,
  modelOffRedSwitchSx,
  settingsPrimaryButtonClassName,
  settingsReadOnlyInputClassName,
  settingsRowDeleteButtonClassName,
  settingsRowEditButtonClassName,
  settingsSectionCardClassName,
  settingsSectionDescriptionClassName,
  settingsSectionTitleClassName,
  settingsTableHeaderClassName,
  switchSx,
} from "../common";
import {
  ChamberSettingsFormState,
  AccountAllocation,
  MemberBenefit,
  MembershipAdditionalCost,
  MembershipEmployeeRange,
  MembershipModel,
  PaymentBeforeApprovalRule,
  PaymentSchedule,
  membershipAdditionalCosts,
} from "../settingsData";
import {
  getDerivedMonthlyMembershipPrice,
  getMembershipEmployeeRangeCode,
  isAutoMembershipEmployeeRangeCode,
  isFinalMembershipEmployeeRange,
  normalizeFinalMembershipEmployeeRange,
  normalizeFormerFinalMembershipEmployeeRange,
  normalizeMembershipModelsForForm,
} from "../settingsMappers";
import { getMembershipEmployeeRangeIssues } from "../settingsMembershipRangeValidation";
import MembershipOptionsIntroField from "./MembershipOptionsIntroField";
import MonthYearPicker from "../common/MonthYearPicker";
import {
  formatMoneyValueForDisplay,
  sanitizeMoneyInput,
} from "../common/numericInputs";
import {
  ProrationCell,
  formatProrationMonths,
  getProratedAmountInDollars,
  getProrationCells,
  getProrationEndLabel,
  getProrationMonthsForEndLabel,
  isProratedPricingActive,
} from "../settingsProration";

type RangeField = "from" | "to" | "annualPrice";
type MembershipSettings = ChamberSettingsFormState["membership"];

type MembershipTabProps = {
  settings: MembershipSettings;
  // Global Settings values that drive prorated fees (fiscal start, Fix Date,
  // Multi-Billing Dates). Read only here.
  prorationSetup?: ChamberSettingsFormState["setup"];
  accountAllocations: AccountAllocation[];
  onChange: (settings: MembershipSettings) => void;
  sectionControls: SectionSaveResetProps;
};

type MembershipTypeModalState = {
  mode: "add" | "edit";
  model: MembershipModel;
};

type AddOnModalState = {
  mode: "add" | "edit" | "editFee";
  addOn: MembershipAdditionalCost;
};

// Local-only details of an icon chosen in the tier modal (design (11) meta
// line). A saved icon has no stored file name or size.
type TierIconFile = {
  name: string;
  size: number;
};

const paymentScheduleOptions: {
  id: PaymentSchedule;
  label: string;
}[] = [
  { id: "annual", label: "Annually" },
  { id: "monthly", label: "Monthly" },
];

// Design (11): six tier slots by default; "+ Add Additional Membership Tiers"
// appends Tier 7 and later, up to the backend limit of 12.
const tierModelIdPattern = /^tier-([1-9]|1[0-2])$/;
const defaultMembershipTierSlots = 6;

const primaryButtonClassName = settingsPrimaryButtonClassName;
// Claude Design Membership Settings controls (D:6105, 6110, 6156, 7561).
const addRowButtonClassName =
  "inline-flex w-fit items-center justify-center rounded-[8px] border-0 bg-[#E1A731] px-[16px] py-[10px] text-[13px] font-semibold text-white transition hover:bg-[#C6912A] disabled:cursor-not-allowed disabled:opacity-50";
const editButtonClassName = settingsRowEditButtonClassName;
const dangerButtonClassName = settingsRowDeleteButtonClassName;
const modalSaveButtonClassName =
  "inline-flex items-center justify-center rounded-[8px] border-0 bg-[#E1A731] px-[42px] py-[13px] text-[15px] font-bold text-white transition hover:bg-[#C6912A] disabled:cursor-not-allowed disabled:opacity-50";
const modalInputClassName =
  "w-full rounded-[8px] border border-[#ECECEC] bg-white px-[14px] py-[12px] text-[14px] text-[#0A0A0A] outline-none transition focus:border-cpaGold focus:ring-2 focus:ring-amber-100";
const modalTextareaClassName = `${modalInputClassName} h-[96px] resize-y`;
const compactInputClassName = settingsReadOnlyInputClassName;
// The header and every benefit row must share the track definition, gap, and
// horizontal padding. Any row-only geometry shifts the fixed tier tracks away
// from their headings because the flexible benefit-description track absorbs
// the difference.
const tierBenefitsGridClassName = "grid gap-3 px-5";
const getTierBenefitsGridStyle = (tierCount: number): React.CSSProperties => ({
  gridTemplateColumns: `minmax(360px,1fr) repeat(${tierCount},76px) 100px`,
});
const membershipSetupCardClassName = settingsSectionCardClassName;
const membershipSetupHeaderClassName = settingsSectionTitleClassName;
const membershipSetupDescriptionClassName = settingsSectionDescriptionClassName;
const membershipTableRowClassName =
  "grid gap-4 px-5 py-5";
const membershipTableHeaderClassName = `${settingsTableHeaderClassName} gap-4`;
const membershipGlobalInputClassName =
  "w-16 rounded-[8px] border border-[#ECECEC] bg-white px-3 py-[9px] text-center text-sm text-[#0A0A0A] outline-none transition focus:border-cpaGold focus:ring-2 focus:ring-amber-100 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400";
const disabledModelSwitchSx = modelOffRedSwitchSx;

// Step 6 "Membership Application Conditions" options, verbatim from CMP
// Dashboard (22) L8805-8824 (Monday 13305307965, 13335776351).
const membershipStatusOptions: {
  value: PaymentBeforeApprovalRule;
  title: string;
  description: string;
}[] = [
  {
    value: "Allow payment before approval",
    title: "Submit Application and Pay Now or Request an Invoice",
    description:
      "Select this option to allow an applicant to submit a membership application and make payment via credit card, or request an invoice and pay with any payment option enabled by the chamber. The potential member will still require board approval before the member status is Active. If payment is made and approval is denied, a refund will need to be issued.",
  },
  {
    value: "Collect payment after approval",
    title: "Submit Application and Send Invoice After Board Approval",
    description:
      "Select this option to allow the applicant to submit a membership application; no invoice will be generated until the applicant is approved. The potential member will still require board approval before the member status is Active. Once marked as approved, the invoice will be automatically generated and sent.",
  },
  {
    value: "Card payment required to submit",
    title: "Submit Application with Credit Card Payment Only",
    description:
      "Select this option to allow the applicant to submit a membership application only when they have submitted payment via credit card. The potential member will still require board approval before the member status is Active. If approval is denied, a refund will need to be issued.",
  },
];

// Step 6 option 2 sends the invoice after approval, so it needs Step 5 where
// the new rules are not offered yet (Monday 13335776351).
const approvalRequiredMembershipStatusRule: PaymentBeforeApprovalRule =
  "Collect payment after approval";
export const getMembershipStatusNeedsApprovalText = (approvalTitle: string) =>
  `This option sends the invoice after approval, so approval must be turned on. To use it, tick "${approvalTitle}" in Step 5 Membership Approval.`;

// Design copy (D:6123, D:6352). Verbatim except the approved typo list.
const tierBenefitsDescription =
  "Create and manage your membership benefits that will be displayed on your chamber website under Membership Benefits. For tiered membership models you can select which benefits are available under each tier by simply checking the box next to the benefit available for each membership model tier. For non tiered membership models check Show all benefits.";
const sizeBenefitsDescription =
  "Create and manage the membership benefits displayed on your chamber website under Membership Benefits in the Membership Options module. You can also edit the introductory description that appears above the list of benefits.";
// Design (11) prorated blocks (D:7043, D:7298).
const proratedMembershipDescription =
  "Prorated membership have been auto generated based on your fiscal year set up in chamber set up global settings. To change the prorated amounts or extend the membership period, edit the fields below and the memberships will be invoiced accordingly.";
const maxMembershipTiers = 12;
const tierIconMaxBytes = 2 * 1024 * 1024;
const tierIconMimeTypes = ["image/png", "image/jpeg", "image/svg+xml"];
// Design (11) add-on table tracks (D:6970, D:7232).
const addOnTableColumnsClassName =
  "grid-cols-[minmax(70px,.5fr)_minmax(300px,2.4fr)_minmax(110px,.9fr)_minmax(125px,1fr)_minmax(135px,1.1fr)_minmax(160px,1fr)]";

const defaultCompanyRanges: Array<
  Pick<MembershipEmployeeRange, "id" | "code" | "from" | "to">
> = [
  { id: "company-1", code: "CM2-4", from: "2", to: "4" },
  { id: "company-2", code: "CM5-10", from: "5", to: "10" },
  { id: "company-3", code: "CM11-25", from: "11", to: "25" },
  { id: "company-4", code: "CM26-50", from: "26", to: "50" },
  { id: "company-5", code: "CM51-100", from: "51", to: "100" },
  { id: "company-6", code: "CM101-200", from: "101", to: "200" },
  { id: "company-7", code: "CM201-300", from: "201", to: "300" },
  { id: "company-8", code: "CM301-500", from: "301", to: "500" },
  { id: "company-9", code: "CM501+", from: "501", to: "" },
];

const getStableTimestampId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

const isTierModel = (membershipModel: MembershipModel) =>
  tierModelIdPattern.test(membershipModel.id);

const parseDollars = (value: string) => {
  const parsedValue = parseFloat(value.replace(/[^0-9.-]/g, ""));
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const formatDollars = (value: number) => `$${value.toFixed(2)}`;

// The design shows an unset member target as "00" (D:6096, D:6325).
const formatMemberTarget = (memberTarget: number) =>
  memberTarget > 0 ? String(memberTarget) : "00";

const getFinalRangeInputValue = (range: MembershipEmployeeRange) =>
  range.from.trim() ? `${range.from}+` : "";

const normalizeMembershipModelPositions = (
  membershipModels: MembershipModel[]
) =>
  membershipModels.map((membershipModel, index) => ({
    ...membershipModel,
    position:
      Number.isFinite(membershipModel.position) && membershipModel.position > 0
        ? membershipModel.position
        : index + 1,
  }));

const createNewRange = (
  membershipModelId: string,
  index: number
): MembershipEmployeeRange => ({
  id: `${membershipModelId}-range-${Date.now()}-${index}`,
  code: "",
  from: "",
  to: "",
  annualPrice: "$0.00",
  monthlyPrice: "$0.00",
  memberTarget: 0,
});

const createCompanyMembershipModel = (): MembershipModel => ({
  id: "company",
  code: "CM",
  name: "Company Membership",
  description: "For companies based on number of employees.",
  memberTarget: 0,
  enabled: true,
  publicMembershipApplicationEnabled: true,
  position: 1,
  paymentResponsibility: "Member/applicant pays",
  thirdPartyPayerName: "",
  thirdPartyPaymentNotes: "",
  pricingModel: "employeeRange",
  annualPrice: "$0.00",
  monthlyPrice: "$0.00",
  includedBenefitIds: [],
  additionalCosts: membershipAdditionalCosts.map((addOn) => ({ ...addOn })),
  ranges: defaultCompanyRanges.map((range) => ({
    ...range,
    annualPrice: "$0.00",
    monthlyPrice: "$0.00",
  })),
});

const createTierMembershipModel = (tierNumber: number): MembershipModel => ({
  id: `tier-${tierNumber}`,
  code: `Tier ${tierNumber}`,
  name: "Membership Tier Name",
  description: "Membership Tier Description",
  memberTarget: 0,
  enabled: tierNumber <= 3,
  publicMembershipApplicationEnabled: tierNumber <= 3,
  position: tierNumber,
  paymentResponsibility: "Member/applicant pays",
  thirdPartyPayerName: "",
  thirdPartyPaymentNotes: "",
  pricingModel: "fixed",
  annualPrice: "$0.00",
  monthlyPrice: "$0.00",
  includedBenefitIds: [],
  additionalCosts: [],
  ranges: [],
});

const createNewBenefit = (): MemberBenefit => ({
  id: getStableTimestampId("benefit"),
  label: "",
});

const createNewAddOn = (): MembershipAdditionalCost => ({
  id: getStableTimestampId("add-on"),
  code: "",
  name: "",
  enabled: true,
  annualAmount: "",
  monthlyAmount: "",
  accountNumber: "",
  description: "",
  feeType: "fixed",
  percentage: "",
  taxExempt: false,
});

const isPercentageAddOn = (addOn: MembershipAdditionalCost) =>
  addOn.feeType === "percentage";

// Employee-range memberships have no single fee, so a percentage add-on
// applies to tiers and fixed-fee membership types only (backend rule).
const getAddOnsForMembershipModel = (
  membershipModel: Pick<MembershipModel, "pricingModel">,
  addOns: MembershipAdditionalCost[]
) =>
  membershipModel.pricingModel === "fixed"
    ? addOns
    : addOns.filter((addOn) => !isPercentageAddOn(addOn));

// Same rounding as the backend (round(fee x percentage) / 100).
const getPercentageAddOnAmount = (annualPrice: string, percentage: string) =>
  Math.round(parseDollars(annualPrice) * parseDollars(percentage)) / 100;

const formatPercentage = (percentage: string) =>
  `${parseDollars(percentage)}%`;

// Design (11) eligibility cell (D:15843-15849).
const getTierEligibilityText = (membershipModel: MembershipModel) => {
  const from = (membershipModel.eligibilityEmployeesFrom || "").trim();
  const to = (membershipModel.eligibilityEmployeesTo || "").trim();
  if (from && to) return `${from} to ${to} Employees`;
  if (to) return `1 to ${to} Employees`;
  return "No employee size restriction.";
};

const formatTierIconSize = (size: number) =>
  size < 1024 * 1024
    ? `${Math.max(1, Math.round(size / 1024))} KB`
    : `${(size / (1024 * 1024)).toFixed(1)} MB`;

const readFileAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const ToggleStatus = ({ enabled }: { enabled: boolean }) => (
  <span
    className={`text-[10px] font-bold uppercase tracking-[.04em] ${
      enabled ? "text-[#10B981]" : "text-[#727272]"
    }`}
  >
    {enabled ? "Enabled" : "Disabled"}
  </span>
);

const MembershipGlobalStepNumber = ({ value }: { value: number }) => (
  <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-[#E1A731] text-[12px] font-bold leading-none text-white">
    {value}
  </span>
);

type MembershipModalProps = {
  title: string;
  titleClassName?: string;
  description?: string;
  panelClassName?: string;
  closeLabel: string;
  onClose: () => void;
  children: React.ReactNode;
};

// Claude Design form modal (D:7530-7669): 14px radius, red round close,
// centred title, centred gold Save.
const MembershipModal = ({
  title,
  titleClassName = "text-[14px] font-semibold",
  description,
  panelClassName = "w-[750px] px-[34px] py-[32px]",
  closeLabel,
  onClose,
  children,
}: MembershipModalProps) => (
  <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[rgba(10,10,10,0.45)] p-6">
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className={`relative flex max-h-[92vh] max-w-full flex-col gap-[20px] overflow-y-auto rounded-[14px] border border-[#ECECEC] bg-white text-[#0A0A0A] shadow-[0_18px_48px_rgba(0,0,0,0.18)] ${panelClassName}`}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label={closeLabel}
        className="absolute right-[14px] top-[14px] flex h-[28px] w-[28px] items-center justify-center rounded-full border-0 bg-[#F21B1B] text-white"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className="h-[13px] w-[13px]" aria-hidden="true">
          <path d="m6 6 12 12M18 6 6 18" />
        </svg>
      </button>
      <div className="flex flex-col items-center gap-[8px] px-[24px] text-center">
        <h2 className={titleClassName}>{title}</h2>
        {description ? <p className="text-[14px]">{description}</p> : null}
      </div>
      {children}
    </div>
  </div>
);

type MembershipModalFieldProps = {
  label: string;
  required?: boolean;
  optional?: boolean;
  helper?: React.ReactNode;
  counter?: string;
  labelClassName?: string;
  children: React.ReactNode;
};

const MembershipModalField = ({
  label,
  required = false,
  optional = false,
  helper,
  counter,
  labelClassName = "text-[13px] font-bold tracking-[.02em]",
  children,
}: MembershipModalFieldProps) => (
  <label className="flex flex-col gap-[8px]">
    <span className={labelClassName}>
      {label}
      {required ? <span className="text-[#E24C4C]">*</span> : null}
      {optional ? (
        <span className="font-normal tracking-normal text-[#727272]"> (Optional)</span>
      ) : null}
    </span>
    {helper ? <span className="text-[14px]">{helper}</span> : null}
    {children}
    {counter ? <span className="text-[14px] text-[#727272]">{counter}</span> : null}
  </label>
);

const tierModalLabelClassName = "text-[14px] font-semibold tracking-[.02em]";

const MembershipGlobalToggleStatus = ({ enabled }: { enabled: boolean }) => (
  <span
    className={`text-[12px] font-semibold uppercase leading-none tracking-[.04em] ${
      enabled ? "text-[#10B981]" : "text-[#727272]"
    }`}
  >
    {enabled ? "Enabled" : "Disabled"}
  </span>
);

const getTierNumber = (membershipModel: MembershipModel) =>
  Number(membershipModel.id.replace("tier-", ""));

const isCompanyMembershipModel = (membershipModel: MembershipModel) =>
  membershipModel.id === "company" ||
  membershipModel.pricingModel === "employeeRange";

const MembershipTab = ({
  settings,
  prorationSetup,
  accountAllocations,
  onChange,
  sectionControls,
}: MembershipTabProps) => {
  const noun = useCmpOrganizationNoun();
  const [membershipTypeModal, setMembershipTypeModal] =
    useState<MembershipTypeModalState | null>(null);
  const [membershipTypeModalError, setMembershipTypeModalError] = useState("");
  const [benefitDeleteCandidateId, setBenefitDeleteCandidateId] = useState<
    string | null
  >(null);
  const [addOnModal, setAddOnModal] = useState<AddOnModalState | null>(null);
  const [addOnModalError, setAddOnModalError] = useState("");
  const [addOnSuccess, setAddOnSuccess] = useState<{
    message: string;
    name: string;
  } | null>(null);
  const [tierIconFile, setTierIconFile] = useState<TierIconFile | null>(null);
  const [tierIconError, setTierIconError] = useState("");
  const [tierIconDragging, setTierIconDragging] = useState(false);
  const [addOnDeleteCandidateId, setAddOnDeleteCandidateId] = useState<
    string | null
  >(null);

  const {
    enabledPaymentSchedules,
    partTimeEmployeesPerFullTime,
    memberBenefits,
    membershipModels,
    proratedPrices,
    globalToggleState,
    paymentBeforeApprovalRule,
    membershipApprovalRulesAvailable = false,
  } = settings;

  const enabledScheduleCount = paymentScheduleOptions.filter(
    (schedule) => enabledPaymentSchedules[schedule.id]
  ).length;

  const companyMembershipModel = useMemo(
    () =>
      membershipModels.find((model) => model.id === "company") ||
      membershipModels.find((model) => model.pricingModel === "employeeRange"),
    [membershipModels]
  );

  const fixedMembershipModels = useMemo(
    () =>
      membershipModels.filter(
        (model) => model.pricingModel === "fixed" && !isTierModel(model)
      ),
    [membershipModels]
  );

  const companyRangeIssues = getMembershipEmployeeRangeIssues(
    companyMembershipModel?.ranges || []
  );

  const enabledFixedMembershipModels = useMemo(
    () =>
      fixedMembershipModels.filter(
        (model) => model.enabled && model.publicMembershipApplicationEnabled
      ),
    [fixedMembershipModels]
  );

  const actualTierMembershipModels = useMemo(
    () => membershipModels.filter(isTierModel),
    [membershipModels]
  );

  const tierMembershipModels = useMemo(() => {
    const existingTierModelsById = new Map(
      actualTierMembershipModels.map((model) => [model.id, model] as const)
    );

    const tierSlotCount = Math.max(
      defaultMembershipTierSlots,
      ...actualTierMembershipModels.map(getTierNumber)
    );

    return Array.from({ length: tierSlotCount }, (_, index) => {
      const tierNumber = index + 1;
      return (
        existingTierModelsById.get(`tier-${tierNumber}`) ||
        createTierMembershipModel(tierNumber)
      );
    });
  }, [actualTierMembershipModels]);

  const enabledTierMembershipModels = useMemo(
    () =>
      tierMembershipModels.filter(
        (model) => model.enabled && model.publicMembershipApplicationEnabled
      ),
    [tierMembershipModels]
  );

  const enabledNonTierModelExists = useMemo(
    () => membershipModels.some((model) => !isTierModel(model) && model.enabled),
    [membershipModels]
  );
  const tierSetupActive =
    actualTierMembershipModels.some((model) => model.enabled) ||
    (actualTierMembershipModels.length > 0 && !enabledNonTierModelExists);

  // Add-ons are shared by every membership model. An employee-range model
  // holds only the fixed add-ons, so read the longest list.
  const addOns = useMemo(
    () =>
      membershipModels.reduce<MembershipAdditionalCost[]>(
        (longest, model) =>
          model.additionalCosts.length > longest.length
            ? model.additionalCosts
            : longest,
        []
      ),
    [membershipModels]
  );

  const accountNumberOptions = useMemo(
    () =>
      Array.from(
        new Set(
          [
            ...accountAllocations.map((account) => account.accountNumber),
            ...addOns.map((addOn) => addOn.accountNumber),
            "4000",
          ].filter((accountNumber) => accountNumber.trim())
        )
      ),
    [accountAllocations, addOns]
  );

  const setMembershipModels = (
    valueOrUpdater:
      | MembershipModel[]
      | ((current: MembershipModel[]) => MembershipModel[])
  ) => {
    onChange({
      ...settings,
      membershipModels: normalizeMembershipModelsForForm(
        normalizeMembershipModelPositions(
          typeof valueOrUpdater === "function"
            ? valueOrUpdater(membershipModels)
            : valueOrUpdater
        )
      ),
    });
  };

  const activateSizeBasedSetup = () => {
    setMembershipModels((current) => {
      const currentWithCompany = current.some(isCompanyMembershipModel)
        ? current
        : [createCompanyMembershipModel(), ...current];
      let companyModelWasEnabled = false;

      return currentWithCompany.map((membershipModel) => {
        if (isTierModel(membershipModel)) {
          return {
            ...membershipModel,
            enabled: false,
            publicMembershipApplicationEnabled: false,
          };
        }

        if (isCompanyMembershipModel(membershipModel) && !companyModelWasEnabled) {
          companyModelWasEnabled = true;
          return {
            ...membershipModel,
            enabled: true,
            publicMembershipApplicationEnabled: true,
          };
        }

        return membershipModel;
      });
    });
  };

  const activateTierBasedSetup = () => {
    setMembershipModels((current) => {
      const existingTierModelIds = new Set(
        current.filter(isTierModel).map((model) => model.id)
      );
      const missingTierModels = Array.from({ length: 6 }, (_, index) =>
        createTierMembershipModel(index + 1)
      ).filter((tierModel) => !existingTierModelIds.has(tierModel.id));
      const nextModels = [...current, ...missingTierModels];

      return nextModels.map((membershipModel) =>
        isTierModel(membershipModel)
          ? {
              ...membershipModel,
              enabled: getTierNumber(membershipModel) <= 3,
              publicMembershipApplicationEnabled:
                getTierNumber(membershipModel) <= 3,
            }
          : {
              ...membershipModel,
              enabled: false,
              publicMembershipApplicationEnabled: false,
            }
      );
    });
  };

  const updateMembershipModel = (
    id: string,
    updater: (membershipModel: MembershipModel) => MembershipModel
  ) => {
    setMembershipModels((current) =>
      current.map((membershipModel) =>
        membershipModel.id === id ? updater(membershipModel) : membershipModel
      )
    );
  };

  const updatePaymentScheduleEnabled = (
    scheduleId: PaymentSchedule,
    enabled: boolean
  ) => {
    if (
      !enabled &&
      enabledPaymentSchedules[scheduleId] &&
      enabledScheduleCount === 1
    ) {
      return;
    }

    const nextSchedules = {
      ...enabledPaymentSchedules,
      [scheduleId]: enabled,
    };

    onChange({
      ...settings,
      enabledPaymentSchedules: nextSchedules,
      globalToggleState: {
        ...globalToggleState,
        prorate: nextSchedules.annual ? globalToggleState.prorate : false,
      },
    });
  };

  const updateGlobalToggleState = (
    updater: (
      currentToggleState: MembershipSettings["globalToggleState"]
    ) => MembershipSettings["globalToggleState"]
  ) => {
    onChange({
      ...settings,
      globalToggleState: updater(globalToggleState),
    });
  };

  const updateRange = (
    membershipModelId: string,
    rangeId: string,
    field: RangeField,
    value: string
  ) => {
    updateMembershipModel(membershipModelId, (membershipModel) => ({
      ...membershipModel,
      ranges: membershipModel.ranges.map((range, index, ranges) => {
        if (range.id !== rangeId) {
          return range;
        }

        const finalRange = isFinalMembershipEmployeeRange(ranges, index);
        const nextRange = {
          ...range,
          [field]: value,
          ...(finalRange && field === "from" ? { to: "" } : {}),
        };

        return field === "from" || field === "to"
          ? {
              ...nextRange,
              code: isAutoMembershipEmployeeRangeCode(range)
                ? getMembershipEmployeeRangeCode(
                    {
                      from: nextRange.from,
                      to: finalRange ? "" : nextRange.to,
                    },
                    { openEnded: finalRange }
                  )
                : range.code,
            }
          : nextRange;
      }),
    }));
  };

  const addRange = (membershipModelId: string) => {
    updateMembershipModel(membershipModelId, (membershipModel) => ({
      ...membershipModel,
      ranges: [
        ...membershipModel.ranges.map((range, index, ranges) =>
          isFinalMembershipEmployeeRange(ranges, index)
            ? normalizeFormerFinalMembershipEmployeeRange(range)
            : range
        ),
        createNewRange(membershipModelId, membershipModel.ranges.length),
      ],
    }));
  };

  const deleteRange = (membershipModelId: string, rangeId: string) => {
    updateMembershipModel(membershipModelId, (membershipModel) => ({
      ...membershipModel,
      ranges:
        membershipModel.ranges.length > 1
          ? membershipModel.ranges
              .filter((range) => range.id !== rangeId)
              .map((range, index, ranges) =>
                isFinalMembershipEmployeeRange(ranges, index)
                  ? normalizeFinalMembershipEmployeeRange(range)
                  : range
              )
          : membershipModel.ranges,
    }));
  };

  const updateProratedPrices = (
    nextPrices: MembershipSettings["proratedPrices"],
    nextPeriodMonths?: MembershipSettings["proratedPeriodMonths"]
  ) => {
    onChange({
      ...settings,
      proratedPrices: nextPrices,
      ...(nextPeriodMonths ? { proratedPeriodMonths: nextPeriodMonths } : {}),
    });
  };

  const createCompanyModelIfNeeded = () => {
    if (companyMembershipModel) {
      return;
    }

    setMembershipModels((current) => [
      createCompanyMembershipModel(),
      ...current,
    ]);
  };

  const applySharedAddOns = (nextAddOns: MembershipAdditionalCost[]) => {
    setMembershipModels((current) =>
      current.map((membershipModel) => ({
        ...membershipModel,
        additionalCosts: getAddOnsForMembershipModel(membershipModel, nextAddOns),
      }))
    );
  };

  const saveMembershipTypeModal = () => {
    if (!membershipTypeModal) {
      return;
    }

    const trimmedName = membershipTypeModal.model.name.trim();
    const trimmedCode = membershipTypeModal.model.code.trim();
    const trimmedDescription = membershipTypeModal.model.description.trim();
    const isTier = isTierModel(membershipTypeModal.model);
    // The design marks code, name, and description as required for both
    // tiers and types (D:7574-7584, D:7610-7619).
    if (!trimmedName || !trimmedCode || !trimmedDescription) {
      setMembershipTypeModalError(
        isTier
          ? "Code, Membership Tier Name and Description are required."
          : "Enter a Code, Membership Type Name and Membership Type Description."
      );
      return;
    }

    // Design (11): From without To means no restriction (D:15862-15863).
    const eligibilityTo = (
      membershipTypeModal.model.eligibilityEmployeesTo || ""
    ).trim();
    const eligibilityFrom = eligibilityTo
      ? (membershipTypeModal.model.eligibilityEmployeesFrom || "").trim()
      : "";
    if (isTier && Number(eligibilityFrom) === 0 && eligibilityFrom) {
      setMembershipTypeModalError(
        "Number of Employees From must be 1 or more."
      );
      return;
    }
    if (isTier && Number(eligibilityTo) === 0 && eligibilityTo) {
      setMembershipTypeModalError("Number of Employees To must be 1 or more.");
      return;
    }
    if (
      isTier &&
      eligibilityFrom &&
      Number(eligibilityTo) < Number(eligibilityFrom)
    ) {
      setMembershipTypeModalError(
        "Number of Employees To must be at least Number of Employees From."
      );
      return;
    }

    const duplicateCode = membershipModels.some(
      (model) =>
        model.id !== membershipTypeModal.model.id &&
        model.code.trim().toLowerCase() === trimmedCode.toLowerCase()
    );
    if (duplicateCode) {
      setMembershipTypeModalError(
        `The code ${trimmedCode} is already used by another membership. Enter a different code.`
      );
      return;
    }

    const nextModel = {
      ...membershipTypeModal.model,
      code: trimmedCode,
      name: trimmedName,
      description: trimmedDescription,
      memberTarget: Math.max(0, membershipTypeModal.model.memberTarget || 0),
      // A model that is new to the saved list gets the shared add-ons.
      additionalCosts: getAddOnsForMembershipModel(
        membershipTypeModal.model,
        addOns
      ),
      ...(isTier
        ? {
            eligibilityEmployeesFrom: eligibilityFrom,
            eligibilityEmployeesTo: eligibilityTo,
          }
        : {}),
      // "+ Add Additional Membership Tiers" appends a new tier, so the added
      // tier is offered as soon as it is saved.
      ...(isTier && membershipTypeModal.mode === "add"
        ? { enabled: true, publicMembershipApplicationEnabled: true }
        : {}),
    };

    // Upsert: chamber-created membership types (CS-008) do not exist in the
    // list yet when the modal saves, so append instead of no-op mapping.
    setMembershipModels((current) =>
      current.some((model) => model.id === nextModel.id)
        ? current.map((model) =>
            model.id === nextModel.id ? nextModel : model
          )
        : [...current, nextModel]
    );

    setMembershipTypeModalError("");
    setMembershipTypeModal(null);
    setTierIconFile(null);
    setTierIconError("");
  };

  const openAddMembershipTypeModal = () => {
    const baseId = getStableTimestampId("membership-type");
    const usedIds = new Set(membershipModels.map((model) => model.id));
    const id = [
      baseId,
      ...membershipModels.map((_, index) => `${baseId}-${index + 1}`),
    ].find((candidate) => !usedIds.has(candidate)) as string;

    setMembershipTypeModalError("");
    setMembershipTypeModal({
      mode: "add",
      model: {
        id,
        code: "",
        name: "",
        description: "",
        memberTarget: 0,
        enabled: false,
        publicMembershipApplicationEnabled: false,
        position:
          Math.max(0, ...membershipModels.map((model) => model.position)) + 1,
        paymentResponsibility: "Member/applicant pays",
        thirdPartyPayerName: "",
        thirdPartyPaymentNotes: "",
        pricingModel: "fixed",
        annualPrice: "$0.00",
        monthlyPrice: "$0.00",
        includedBenefitIds: memberBenefits.map((benefit) => benefit.id),
        additionalCosts: [],
        ranges: [],
      },
    });
  };

  // Design (11): "add" appends the next tier number (Tier 7 and later), up
  // to the backend limit of tier-12.
  const nextTierNumber = tierMembershipModels.length + 1;
  const canAddTier = nextTierNumber <= maxMembershipTiers;

  const openTierModal = (mode: "add" | "edit", model: MembershipModel) => {
    setMembershipTypeModalError("");
    setTierIconFile(null);
    setTierIconError("");
    setMembershipTypeModal({ mode, model });
  };

  const openAddTierModal = () => {
    if (!canAddTier) {
      return;
    }

    openTierModal("add", {
      ...createTierMembershipModel(nextTierNumber),
      code: "",
      name: "",
      description: "",
      annualPrice: "",
      monthlyPrice: "",
      iconUrl: "",
      eligibilityEmployeesFrom: "",
      eligibilityEmployeesTo: "",
    });
  };

  const selectTierIcon = async (file?: File | null) => {
    if (!file) {
      return;
    }
    if (!tierIconMimeTypes.includes(file.type)) {
      setTierIconError("Upload a PNG, JPG or SVG file.");
      return;
    }
    if (file.size > tierIconMaxBytes) {
      setTierIconError("The icon must be 2 MB or smaller.");
      return;
    }
    const dataUrl = await readFileAsDataUrl(file);
    setTierIconError("");
    setTierIconFile({ name: file.name, size: file.size });
    updateModalModel({ iconUrl: dataUrl });
  };

  const removeTierIcon = () => {
    setTierIconError("");
    setTierIconFile(null);
    updateModalModel({ iconUrl: "" });
  };

  const addBenefit = () => {
    const nextBenefit = createNewBenefit();

    onChange({
      ...settings,
      memberBenefits: [...memberBenefits, nextBenefit],
      membershipModels: membershipModels.map((membershipModel) =>
        isTierModel(membershipModel)
          ? membershipModel
          : {
              ...membershipModel,
              includedBenefitIds: Array.from(
                new Set([...membershipModel.includedBenefitIds, nextBenefit.id])
              ),
            }
      ),
    });
  };

  const deleteBenefit = (benefitId: string) => {
    onChange({
      ...settings,
      memberBenefits: memberBenefits.filter((benefit) => benefit.id !== benefitId),
      membershipModels: membershipModels.map((membershipModel) => ({
        ...membershipModel,
        includedBenefitIds: membershipModel.includedBenefitIds.filter(
          (includedBenefitId) => includedBenefitId !== benefitId
        ),
      })),
    });
    setBenefitDeleteCandidateId(null);
  };

  const toggleTierBenefit = (
    membershipModelId: string,
    benefitId: string,
    checked: boolean
  ) => {
    updateMembershipModel(membershipModelId, (membershipModel) => ({
      ...membershipModel,
      includedBenefitIds: checked
        ? Array.from(new Set([...membershipModel.includedBenefitIds, benefitId]))
        : membershipModel.includedBenefitIds.filter((id) => id !== benefitId),
    }));
  };

  const setAllTierBenefits = (checked: boolean) => {
    const benefitIds = memberBenefits.map((benefit) => benefit.id);

    setMembershipModels((current) =>
      current.map((membershipModel) =>
        isTierModel(membershipModel)
          ? {
              ...membershipModel,
              includedBenefitIds: checked ? benefitIds : [],
            }
          : membershipModel
      )
    );
  };

  const closeAddOnModal = () => {
    setAddOnModalError("");
    setAddOnModal(null);
  };

  const getPercentageError = (percentage: string) => {
    const value = parseDollars(percentage);
    return value > 0 && value <= 100
      ? ""
      : "Enter a percentage more than 0 and up to 100.";
  };

  const saveAddOnModal = () => {
    if (!addOnModal) {
      return;
    }

    if (addOnModal.mode === "editFee") {
      const isPercentage = isPercentageAddOn(addOnModal.addOn);
      const percentageError = isPercentage
        ? getPercentageError(addOnModal.addOn.percentage || "")
        : "";
      if (percentageError) {
        setAddOnModalError(percentageError);
        return;
      }
      applySharedAddOns(
        addOns.map((addOn) =>
          addOn.id === addOnModal.addOn.id
            ? isPercentage
              ? { ...addOn, percentage: addOnModal.addOn.percentage }
              : {
                  ...addOn,
                  annualAmount: addOnModal.addOn.annualAmount,
                  monthlyAmount: addOnModal.addOn.annualAmount,
                }
            : addOn
        )
      );
      closeAddOnModal();
      return;
    }

    const isPercentage = isPercentageAddOn(addOnModal.addOn);
    const nextAddOn: MembershipAdditionalCost = {
      ...addOnModal.addOn,
      code: addOnModal.addOn.code.trim(),
      name: addOnModal.addOn.name.trim(),
      accountNumber: addOnModal.addOn.accountNumber.trim(),
      description: addOnModal.addOn.description.trim(),
      annualAmount: isPercentage ? "$0.00" : addOnModal.addOn.annualAmount,
      monthlyAmount: isPercentage ? "$0.00" : addOnModal.addOn.annualAmount,
      percentage: isPercentage ? (addOnModal.addOn.percentage || "").trim() : "",
    };

    // Design (11): only Benefit Name and Benefit Fee are required.
    const fee = isPercentage ? nextAddOn.percentage : nextAddOn.annualAmount;
    if (!nextAddOn.name || !(fee || "").trim()) {
      setAddOnModalError("Benefit Name and Benefit Fee are required.");
      return;
    }
    const percentageError = isPercentage
      ? getPercentageError(nextAddOn.percentage || "")
      : "";
    if (percentageError) {
      setAddOnModalError(percentageError);
      return;
    }

    const isEdit = addOnModal.mode === "edit";
    applySharedAddOns(
      isEdit
        ? addOns.map((addOn) => (addOn.id === nextAddOn.id ? nextAddOn : addOn))
        : [...addOns, nextAddOn]
    );
    closeAddOnModal();
    setAddOnSuccess({
      name: nextAddOn.name,
      message: `The add-on benefit ${nextAddOn.name} has been ${
        isEdit ? "updated" : "added"
      } successfully!`,
    });
  };

  const deleteAddOn = (addOnId: string) => {
    applySharedAddOns(addOns.filter((addOn) => addOn.id !== addOnId));
    setAddOnDeleteCandidateId(null);
  };

  const renderMembershipGlobalSwitch = ({
    checked,
    label,
    onChange: handleChange,
    disabled = false,
    showStatus = false,
  }: {
    checked: boolean;
    label: string;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
    showStatus?: boolean;
  }) => (
    <div className="flex items-center gap-2">
      <Switch
        checked={checked}
        disabled={disabled}
        onChange={(event) => handleChange(event.target.checked)}
        sx={switchSx}
        inputProps={{ "aria-label": label }}
      />
      {showStatus && <MembershipGlobalToggleStatus enabled={checked} />}
    </div>
  );

  const renderSwitch = ({
    checked,
    label,
    onChange: handleChange,
    disabled = false,
  }: {
    checked: boolean;
    label: string;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
  }) => (
    <div className="flex items-center gap-2">
      <Switch
        checked={checked}
        disabled={disabled}
        onChange={(event) => handleChange(event.target.checked)}
        sx={switchSx}
        inputProps={{ "aria-label": label }}
      />
      <ToggleStatus enabled={checked} />
    </div>
  );

  const membershipGlobalRowClassName =
    "flex flex-col gap-[14px] border-t border-[#ECECEC] py-[18px] md:flex-row md:items-start";
  // Monday 13397143172: Steps 5 and 6 keep a 120px right gutter and their
  // options take the rest of the row (CMP Dashboard (24) L8823 and L8851).
  const membershipGlobalOptionRowClassName = `${membershipGlobalRowClassName} md:pr-[120px]`;
  const membershipGlobalTitleClassName =
    "text-[15px] font-semibold text-[#0A0A0A]";
  const membershipGlobalDescriptionClassName =
    "mt-[3px] text-[13px] text-[#0A0A0A]";

  // Layout and copy follow D:5926-6034 verbatim, including the design's
  // "Board of Directors" wording. Obvious design typos are corrected (Q13).
  const renderMembershipGlobalSettings = () => (
    <section className={`${membershipSetupCardClassName} flex flex-col gap-[4px]`}>
      <h2 className={membershipSetupHeaderClassName}>
        Membership Global Settings
      </h2>
      <p className="text-sm text-[#0A0A0A]">
        Enable and set up your membership options.
      </p>

      <div className={`${membershipGlobalRowClassName} mt-[14px]`}>
        <div className="flex flex-1 items-start gap-[14px]">
          <MembershipGlobalStepNumber value={1} />
          <div className="flex-1">
            <h3 className={membershipGlobalTitleClassName}>
              Membership Payment Option
            </h3>
            <p className={membershipGlobalDescriptionClassName}>
              Disable the membership payment options your {noun.singular} does
              not offer.
              <br />
              Monthly payments are made via credit card only.
            </p>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-[40px]">
          {paymentScheduleOptions.map((schedule) => (
            <div key={schedule.id} className="flex items-center gap-[10px]">
              <span className="text-[13px] text-[#0A0A0A]">
                {schedule.label}
              </span>
              {renderMembershipGlobalSwitch({
                checked: enabledPaymentSchedules[schedule.id],
                label: `${schedule.label} payment schedule`,
                disabled:
                  enabledPaymentSchedules[schedule.id] &&
                  enabledScheduleCount === 1,
                onChange: (checked) =>
                  updatePaymentScheduleEnabled(schedule.id, checked),
              })}
            </div>
          ))}
        </div>
      </div>

      <div className={membershipGlobalRowClassName}>
        <div className="flex flex-1 items-start gap-[14px]">
          <MembershipGlobalStepNumber value={2} />
          <div className="flex-1">
            <h3 className={membershipGlobalTitleClassName}>
              Auto-Renewal Settings
            </h3>
            <p className={membershipGlobalDescriptionClassName}>
              Enable auto renewal option to be shown on your {noun.possessive}{" "}
              membership application form.
            </p>
            <p className="mt-[10px] text-[13px] text-[#0A0A0A]">
              Members will see the auto-renewal opt-in at checkout. If a member
              opts in, their credit card will be automatically charged on the
              membership renewal date.
            </p>
          </div>
        </div>
        <div className="shrink-0">
          {renderMembershipGlobalSwitch({
            checked: globalToggleState.autoRenewal,
            label: "Auto-renewal settings",
            showStatus: true,
            onChange: (checked) =>
              updateGlobalToggleState((current) => ({
                ...current,
                autoRenewal: checked,
              })),
          })}
        </div>
      </div>

      <div className={membershipGlobalRowClassName}>
        <div className="flex flex-1 items-start gap-[14px]">
          <MembershipGlobalStepNumber value={3} />
          <div className="flex-1">
            {/* D@913345 item 3 (Monday D14, AE-12, decision F7): design
                text; "Member's on" corrected to "Members on". */}
            <h3 className={membershipGlobalTitleClassName}>
              Send Membership Renewal Invoice and Email For Members Who Have
              Not Selected Auto-Credit Card Renewal Payment
            </h3>
            <p className={membershipGlobalDescriptionClassName}>
              Set how many days before a member's membership expires you want
              the renewal invoice and email to be sent automatically. Members
              on auto credit card renewal payments will have invoice
              automatically generated and credit card charged on the date of
              renewal and payment receipt will be emailed to member.
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-[10px]">
          <WholeNumberInput
            value={settings.renewalNoticeDays}
            onChange={(renewalNoticeDays) =>
              onChange({
                ...settings,
                renewalNoticeDays,
              })
            }
            placeholder="00"
            ariaLabel="Days before renewal date"
            className={membershipGlobalInputClassName}
          />
          <span className="text-[13px] text-[#0A0A0A]">
            days before renewal date
          </span>
        </div>
      </div>

      <div className={membershipGlobalRowClassName}>
        <div className="flex flex-1 items-start gap-[14px]">
          <MembershipGlobalStepNumber value={4} />
          <div className="flex-1">
            <h3 className={membershipGlobalTitleClassName}>
              Send Membership Renewal 1st and 2nd Reminder Email Notification
              that Membership has Not been Renewed
            </h3>
            <p className={membershipGlobalDescriptionClassName}>
              Set how many days before a member's membership is set to expire
              to send reminders.
            </p>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-[10px]">
          <WholeNumberInput
            value={settings.renewalReminderOneDays}
            onChange={(renewalReminderOneDays) =>
              onChange({
                ...settings,
                renewalReminderOneDays,
              })
            }
            placeholder="00"
            ariaLabel="Days before first email reminder"
            className={membershipGlobalInputClassName}
          />
          <span className="text-[13px] text-[#0A0A0A]">1st email reminder</span>
          <WholeNumberInput
            value={settings.renewalReminderTwoDays}
            onChange={(renewalReminderTwoDays) =>
              onChange({
                ...settings,
                renewalReminderTwoDays,
              })
            }
            placeholder="00"
            ariaLabel="Days before second email reminder"
            className={membershipGlobalInputClassName}
          />
          <span className="text-[13px] text-[#0A0A0A]">2nd email reminder</span>
        </div>
      </div>

      {/* IF10 G7 (Monday 13305307965) and Monday 13335776351: Step 5 and
          Step 6 follow CMP Dashboard (22) L8770-8826. All three Step 6 options
          always show (Moeen D5/Q9). The Step 5 payment option and Step 6
          option 2 without approval show for chambers with the new rules. */}
      <div className={membershipGlobalOptionRowClassName}>
        <div className="flex flex-1 items-start gap-[14px]">
          <MembershipGlobalStepNumber value={5} />
          <div className="flex flex-1 flex-col gap-[16px]">
            <div>
              <h3 className={membershipGlobalTitleClassName}>
                Membership Approval
              </h3>
              <p className={membershipGlobalDescriptionClassName}>
                Manage how new memberships are approved and when the potential
                member becomes an Active Member.
              </p>
            </div>
            <label className="flex cursor-pointer items-start gap-[10px]">
              <Checkbox
                checked={globalToggleState.boardApproval}
                onChange={(event) => {
                  const checked = event.target.checked;
                  // Monday 13305307965 (A11): Step 6 option 2 needs Step 5.
                  // Turning Step 5 off moves a chosen option 2 to option 1
                  // here, where staff see it, never silently on save.
                  onChange({
                    ...settings,
                    globalToggleState: { ...globalToggleState, boardApproval: checked },
                    ...(!checked &&
                    !membershipApprovalRulesAvailable &&
                    paymentBeforeApprovalRule === approvalRequiredMembershipStatusRule
                      ? { paymentBeforeApprovalRule: "Allow payment before approval" as const }
                      : {}),
                  });
                }}
                className="mt-[1px]"
                sx={inlineChoiceControlSx}
              />
              <span>
                <span className="block text-sm font-semibold text-[#0A0A0A]">
                  {getCmpBoardWording(noun).approvalSettingTitle}
                </span>
                <span className={`block ${membershipGlobalDescriptionClassName}`}>
                  Check this box if new memberships require approval from either the{" "}
                  {noun.Singular} or the Board of Directors. The membership status will
                  remain Pending Membership Approval until approved. Once approved the
                  membership status will show active.
                </span>
              </span>
            </label>
            {membershipApprovalRulesAvailable && (
              <label className="flex cursor-pointer items-start gap-[10px]">
                <Checkbox
                  checked={globalToggleState.requirePayment}
                  onChange={(event) =>
                    onChange({
                      ...settings,
                      globalToggleState: {
                        ...globalToggleState,
                        requirePayment: event.target.checked,
                      },
                    })
                  }
                  className="mt-[1px]"
                  sx={inlineChoiceControlSx}
                />
                <span>
                  <span className="block text-sm font-semibold text-[#0A0A0A]">
                    New Membership Applications Require Payment to be Paid in Order
                    to be an Active Member
                  </span>
                  <span className={`flex flex-col gap-[6px] ${membershipGlobalDescriptionClassName}`}>
                    <span>
                      Check this box to require payment before a new member becomes
                      Active. The membership status will remain Pending Member Payment
                      until payment is recorded under Manage All Invoices &amp; Payments.
                    </span>
                    <span>
                      Once payment is recorded, member privileges, directory listings
                      and Member Dashboard access are activated. If{" "}
                      {getCmpBoardWording(noun).ApprovalBy} approval is also required,
                      both conditions must be met.
                    </span>
                    <span>
                      Leave unchecked to have Member Status show as Active without
                      requiring payment first.
                    </span>
                  </span>
                </span>
              </label>
            )}
          </div>
        </div>
      </div>

      <div className={membershipGlobalOptionRowClassName}>
        <div className="flex flex-1 items-start gap-[14px]">
          <MembershipGlobalStepNumber value={6} />
          <div className="flex flex-1 flex-col gap-[16px]">
            <div>
              <h3 className={membershipGlobalTitleClassName}>
                Membership Application Conditions{" "}
                <span className="text-[#F0202E]">*</span>
              </h3>
              <p className={membershipGlobalDescriptionClassName}>
                Set the rules for submitting membership applications and for
                when a potential member becomes an Active Member.
              </p>
            </div>
            <div
              role="radiogroup"
              aria-label="Membership Application Conditions"
              className="flex flex-col gap-[16px]"
            >
              {membershipStatusOptions.map((option, index) => {
                const needsApproval =
                  !membershipApprovalRulesAvailable &&
                  option.value === approvalRequiredMembershipStatusRule &&
                  !globalToggleState.boardApproval;
                return (
                <label
                  key={option.value}
                  className={`flex items-start gap-[10px] ${needsApproval ? "cursor-not-allowed" : "cursor-pointer"}`}
                >
                  {/* The visible title names the radio; the description is its help text. */}
                  <Radio
                    name="membershipStatusRule"
                    value={option.value}
                    inputProps={{
                      "aria-labelledby": `membership-status-rule-${index}-title`,
                      "aria-describedby": `membership-status-rule-${index}-description${needsApproval ? ` membership-status-rule-${index}-needs-approval` : ""}`,
                    }}
                    disabled={needsApproval}
                    checked={paymentBeforeApprovalRule === option.value}
                    onChange={() =>
                      onChange({
                        ...settings,
                        paymentBeforeApprovalRule: option.value,
                      })
                    }
                    className="mt-[1px]"
                    sx={inlineChoiceControlSx}
                  />
                  <span>
                    <span
                      id={`membership-status-rule-${index}-title`}
                      className="block text-sm font-semibold text-[#0A0A0A]"
                    >
                      {option.title}
                    </span>
                    <span
                      id={`membership-status-rule-${index}-description`}
                      className={`block ${membershipGlobalDescriptionClassName}`}
                    >
                      {option.description}
                    </span>
                    {needsApproval && (
                      <span
                        id={`membership-status-rule-${index}-needs-approval`}
                        className="mt-[4px] block text-[13px] font-semibold leading-[1.5] text-[#0A0A0A]"
                      >
                        {getMembershipStatusNeedsApprovalText(getCmpBoardWording(noun).approvalSettingTitle)}
                      </span>
                    )}
                  </span>
                </label>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <SectionSaveReset {...sectionControls} />
    </section>
  );

  const renderMembershipModelSelector = () => {
    const sizeBasedEnabled = !tierSetupActive;

    // D:6042-6061: the size card shows its status label before the switch,
    // the tier card after it.
    const cards = [
      {
        title: "Membership-Type (by size)",
        enabled: sizeBasedEnabled,
        statusFirst: true,
        onSelect: activateSizeBasedSetup,
      },
      {
        title: "Membership-Tier",
        enabled: !sizeBasedEnabled,
        statusFirst: false,
        onSelect: activateTierBasedSetup,
      },
    ];

    return (
      <section className={`${membershipSetupCardClassName} flex flex-col gap-[20px]`}>
        <div>
          <h2 className={membershipSetupHeaderClassName}>Membership Model</h2>
          <p className="mt-1 text-sm text-[#0A0A0A]">
            Select and setup your {noun.singular} membership structure and fees.
          </p>
        </div>

        <div className="grid gap-[24px] lg:grid-cols-2">
          {cards.map((card) => {
            const status = (
              <span
                className={`text-[10px] font-bold uppercase tracking-[.04em] ${
                  card.enabled ? "text-[#10B981]" : "text-[#E24C4C]"
                }`}
              >
                {card.enabled ? "Enabled" : "Disabled"}
              </span>
            );
            return (
              <div
                key={card.title}
                role="button"
                tabIndex={0}
                onClick={() => card.onSelect()}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    card.onSelect();
                  }
                }}
                className={`flex cursor-pointer flex-col items-center gap-[8px] rounded-[10px] border-b-2 p-[18px] text-center transition ${
                  card.enabled
                    ? "border-[#E1A731] bg-white text-[#E1A731]"
                    : "border-transparent bg-[#F2F2F3] text-[#727272]"
                }`}
                aria-label={card.title}
                aria-pressed={card.enabled}
              >
                <h3 className="text-base font-semibold">{card.title}</h3>
                <div className="flex items-center justify-center gap-[8px]">
                  {card.statusFirst ? status : null}
                  <Switch
                    checked={card.enabled}
                    size="small"
                    onClick={(event) => event.stopPropagation()}
                    onChange={() => card.onSelect()}
                    sx={card.enabled ? switchSx : disabledModelSwitchSx}
                    inputProps={{ "aria-label": card.title }}
                  />
                  {card.statusFirst ? null : status}
                </div>
              </div>
            );
          })}
        </div>

        <SectionSaveReset {...sectionControls} />
      </section>
    );
  };

  const renderCompanyRangeRows = () => {
    if (!companyMembershipModel) {
      return (
        <div className="mt-8 rounded-lg bg-gray-50 p-6 text-center">
          <p className="text-base font-semibold text-gray-800">
            No company membership model is available yet.
          </p>
          <button
            type="button"
            onClick={createCompanyModelIfNeeded}
            className={`${primaryButtonClassName} mt-4`}
          >
            Create Company Membership
          </button>
        </div>
      );
    }

    return (
      <>
        <div className="mx-auto mt-12 max-w-[84rem]">
          <h3 className="text-lg font-bold text-gray-950">Company Membership</h3>
          <p className="mt-1 text-sm text-gray-950">
            For companies based on number of employees.
          </p>
        </div>

        <div className="mx-auto mt-8 max-w-[84rem] pt-7">
          <h4 className="text-base font-bold text-gray-950">
            Part-Time Employee Formula
          </h4>
          <p className="mt-2 text-sm leading-6 text-gray-950">
            Enter the number of part-time employees that will equal one
            full-time employee. If you do not use a formula just enter 1 = 1
          </p>
          <p className="mt-3 rounded-lg bg-cpaGold px-4 py-2 text-sm font-bold text-white">
            This formula will be used to calculate full-time employee range for
            determining membership fees.
          </p>
          <div className="mt-4 grid gap-4 rounded-xl bg-gray-50 p-5 md:grid-cols-[1fr_auto_1fr] md:items-center">
            <div>
              <WholeNumberInput
                value={partTimeEmployeesPerFullTime}
                onChange={(partTime) =>
                  onChange({
                    ...settings,
                    partTimeEmployeesPerFullTime: partTime,
                  })
                }
                ariaLabel="Part-time employees per full-time employee"
                className={`${compactInputClassName} w-full text-center`}
              />
              <p className="mt-2 text-center text-sm text-gray-900">
                Part-time Employees
              </p>
            </div>
            <span className="text-center text-lg font-bold text-gray-950">
              =
            </span>
            <div>
              <input
                value="1"
                readOnly
                className={`${compactInputClassName} w-full border-gray-200 bg-gray-200 text-center`}
              />
              <p className="mt-2 text-center text-sm text-gray-900">
                Full-time Employee
              </p>
            </div>
          </div>
        </div>

        <div className="mx-auto mt-10 max-w-[84rem]">
          <h4 className="text-base font-bold text-gray-950">
            Company Membership (by Size) Fee Structure
          </h4>
          <p className="mt-2 text-sm leading-6 text-gray-950">
            Set full-time employee range and 12 month membership fee structure by
            simply editing the numbers in range fields and dollar amount fields
            or delete if not required. If additional full-time employee ranges
            are required add range and enter fields.
          </p>
        </div>

        <div className="mx-auto mt-5 max-w-[84rem] overflow-x-auto rounded-lg bg-white">
          <div className="min-w-[1100px]">
            <div
              className={`grid grid-cols-[280px_160px_130px_170px_170px_120px] ${membershipTableHeaderClassName}`}
            >
              <div>Employees Range</div>
              <div>Code</div>
              <div>Member Target</div>
              <div>12 Month Fee</div>
              <div>Monthly Payment</div>
              <div>Actions</div>
            </div>
            {companyMembershipModel.ranges.map((range, index, ranges) => {
              const openEndedRange = isFinalMembershipEmployeeRange(ranges, index);
              const rangeIssue = companyRangeIssues.find((issue) => issue.rangeId === range.id);
              const errorId = `employee-range-${range.id}-error`;

              return (
                <div
                  key={range.id}
                  className={`${membershipTableRowClassName} grid-cols-[280px_160px_130px_170px_170px_120px] items-center`}
                >
                  <div>
                    <div className="flex items-center gap-3">
                      <input
                        aria-label={`Employee range ${index + 1} start`}
                        aria-invalid={Boolean(rangeIssue)}
                        aria-describedby={rangeIssue ? errorId : undefined}
                        value={
                          openEndedRange
                            ? getFinalRangeInputValue(range)
                            : range.from
                        }
                        onChange={(event) =>
                          updateRange(
                            companyMembershipModel.id,
                            range.id,
                            "from",
                            event.target.value.replace("+", "").replace(/[^0-9]/g, "")
                          )
                        }
                        className={`${compactInputClassName} w-20 text-center`}
                      />
                      {!openEndedRange && (
                        <>
                          <span className="text-base font-medium uppercase text-gray-700">
                            TO
                          </span>
                          <input
                            aria-label={`Employee range ${index + 1} end`}
                            aria-invalid={Boolean(rangeIssue)}
                            aria-describedby={rangeIssue ? errorId : undefined}
                            value={range.to}
                            onChange={(event) =>
                              updateRange(
                                companyMembershipModel.id,
                                range.id,
                                "to",
                                event.target.value.replace(/[^0-9]/g, "")
                              )
                            }
                            className={`${compactInputClassName} w-20 text-center`}
                          />
                        </>
                      )}
                    </div>
                    {rangeIssue && (
                      <p id={errorId} role="alert" className="mt-2 text-sm text-red-700">
                        {rangeIssue.message}
                      </p>
                    )}
                  </div>
                  <input
                    value={range.code}
                    readOnly
                    aria-label={`Auto-generated code for range ${range.from || range.id}`}
                    title="Auto-generated from the company size range"
                    className={`${compactInputClassName} text-center`}
                  />
                  {/* D:6269 Member Target per range (employee range memberTarget). */}
                  <WholeNumberInput
                    value={range.memberTarget ? String(range.memberTarget) : ""}
                    placeholder="00"
                    onChange={(value) =>
                      updateMembershipModel(companyMembershipModel.id, (membershipModel) => ({
                        ...membershipModel,
                        ranges: membershipModel.ranges.map((candidate) =>
                          candidate.id === range.id
                            ? { ...candidate, memberTarget: Number(value) || 0 }
                            : candidate
                        ),
                      }))
                    }
                    ariaLabel={`Member target for range ${range.code || range.from || index + 1}`}
                    className={`${compactInputClassName} w-full`}
                  />
                  <MoneyInput
                    value={range.annualPrice}
                    onChange={(value) =>
                      updateRange(
                        companyMembershipModel.id,
                        range.id,
                        "annualPrice",
                        value
                      )
                    }
                    ariaLabel={`Annual fee for range ${range.code || range.from}`}
                  />
                  <TextInput
                    value={getDerivedMonthlyMembershipPrice(range.annualPrice)}
                    readOnly
                    disabled={!enabledPaymentSchedules.monthly}
                    onChange={() => undefined}
                  />
                  <button
                    type="button"
                    disabled={companyMembershipModel.ranges.length <= 1}
                    onClick={() => deleteRange(companyMembershipModel.id, range.id)}
                    className={`${dangerButtonClassName} justify-self-start`}
                  >
                    Delete
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <button
          type="button"
          onClick={() => addRange(companyMembershipModel.id)}
          className={`${addRowButtonClassName} mt-5`}
        >
          + Add Range
        </button>
      </>
    );
  };

  const renderSizeBasedSettings = () => (
    <section className={membershipSetupCardClassName}>
      <div>
        <h2 className={membershipSetupHeaderClassName}>
          Create Membership Types (by Size)
        </h2>
        <p className={membershipSetupDescriptionClassName}>
          Enable the various membership types offered by your {noun.singular} including
          setting up your employee size range and fees which will be shown on
          your website and in your member application form.
        </p>
      </div>

      {renderCompanyRangeRows()}

      <div className="mt-10">
        <h3 className="text-[15px] font-semibold text-[#0A0A0A]">
          Add Additional Membership Types
        </h3>
        <p className="mt-[3px] text-[13px] text-[#0A0A0A]">
          Enable the additional membership types offered by your {noun.singular}. Edit
          any of the listed membership type description or fees below. If
          Enabled and no price is entered the fee will say "Call for Pricing".
          You can also Create a New Membership type by clicking Add New
          Membership Type and Enable.
        </p>
      </div>

      <div className="mt-6 overflow-x-auto">
        <div className="min-w-[1120px]">
          <div
            className={`grid grid-cols-[120px_minmax(300px,1.6fr)_150px_170px_210px_130px] ${membershipTableHeaderClassName}`}
          >
            <div>Code</div>
            <div>Membership Type Description</div>
            <div>Member Target</div>
            <div>Membership Fee</div>
            <div>Enable Membership</div>
            <div>Actions</div>
          </div>
          {fixedMembershipModels.map((membershipModel) => (
            <div
              key={membershipModel.id}
              className={`${membershipTableRowClassName} grid-cols-[120px_minmax(300px,1.6fr)_150px_170px_210px_130px] items-center`}
            >
              <div className="text-[13px] text-[#0A0A0A]">
                {membershipModel.code}
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[#0A0A0A]">
                  {membershipModel.name}
                </h3>
                <p className="text-[13px] text-[#0A0A0A]">
                  {membershipModel.description}
                </p>
              </div>
              <div className="text-sm text-[#0A0A0A]">
                {formatMemberTarget(membershipModel.memberTarget)}
              </div>
              <div className="text-sm text-[#0A0A0A]">
                {parseDollars(membershipModel.annualPrice) > 0
                  ? membershipModel.annualPrice
                  : membershipModel.enabled
                  ? "Call for Pricing"
                  : membershipModel.annualPrice || "$0.00"}
              </div>
              {renderSwitch({
                checked: membershipModel.enabled,
                label: `${membershipModel.name} enabled`,
                onChange: (checked) =>
                  updateMembershipModel(membershipModel.id, (model) => ({
                    ...model,
                    enabled: checked,
                    publicMembershipApplicationEnabled: checked,
                  })),
              })}
              <button
                type="button"
                onClick={() => {
                  setMembershipTypeModalError("");
                  setMembershipTypeModal({
                    mode: "edit",
                    model: membershipModel,
                  });
                }}
                className={`${editButtonClassName} justify-self-start`}
              >
                Edit
              </button>
            </div>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={openAddMembershipTypeModal}
        className={`${addRowButtonClassName} mt-6`}
      >
        + Add Additional Membership Types
      </button>

      <SectionSaveReset {...sectionControls} />
    </section>
  );

  // Design (11) tier table tracks (D:6869).
  const tierTableColumnsClassName =
    "grid-cols-[minmax(65px,.5fr)_minmax(55px,.4fr)_minmax(200px,1.8fr)_minmax(100px,.8fr)_minmax(120px,.9fr)_minmax(145px,1.1fr)_minmax(90px,.7fr)]";
  const savedTierModelIds = new Set(
    actualTierMembershipModels.map((model) => model.id)
  );
  // Design (11): a named tier without a price shows "Call for Pricing".
  const getTierPriceText = (tierModel: MembershipModel) =>
    parseDollars(tierModel.annualPrice) > 0
      ? tierModel.annualPrice
      : savedTierModelIds.has(tierModel.id)
        ? "Call for Pricing"
        : "$0.00";

  const renderTierSettings = () => (
    <section className={membershipSetupCardClassName}>
      <div>
        <h2 className={membershipSetupHeaderClassName}>
          Create Membership Tiers
        </h2>
        <p className={membershipSetupDescriptionClassName}>
          Edit and enable the number of membership tiers your {noun.singular} offers.
          Add a simple description for each tier, and remove or add tiers as
          needed. Membership Tier benefits are added below.
        </p>
      </div>

      <div className="mt-6 overflow-x-auto">
        <div className="min-w-[1040px]">
          <div
            className={`grid ${tierTableColumnsClassName} ${membershipTableHeaderClassName}`}
          >
            <div>Code</div>
            <div>Tier</div>
            <div>Membership Tier Description</div>
            <div>Membership and Employee Range Eligibility</div>
            <div>Membership Fee</div>
            <div>Enable Membership</div>
            <div>Actions</div>
          </div>
          {tierMembershipModels.map((tierModel, index) => (
            <div
              key={tierModel.id}
              className={`${membershipTableRowClassName} ${tierTableColumnsClassName} items-center`}
            >
              <div className="text-[13px] text-[#0A0A0A]">{tierModel.code}</div>
              <div className="text-sm font-semibold text-[#0A0A0A]">
                {index + 1}
              </div>
              <div className="flex min-w-0 items-start gap-[12px]">
                {tierModel.iconUrl ? (
                  <img
                    src={tierModel.iconUrl}
                    alt=""
                    className="h-[40px] w-[40px] shrink-0 rounded-[8px] border border-[#ECECEC] bg-white object-contain"
                  />
                ) : null}
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-[#0A0A0A]">
                    {tierModel.name}
                  </h3>
                  <p className="text-[13px] text-[#0A0A0A]">
                    {tierModel.description}
                  </p>
                </div>
              </div>
              <div className="text-[13px] text-[#0A0A0A]">
                {getTierEligibilityText(tierModel)}
              </div>
              <div className="text-sm text-[#0A0A0A]">
                {getTierPriceText(tierModel)}
              </div>
              {renderSwitch({
                checked: tierModel.enabled,
                label: `${tierModel.name} enabled`,
                onChange: (checked) =>
                  updateMembershipModel(tierModel.id, (model) => ({
                    ...model,
                    enabled: checked,
                    publicMembershipApplicationEnabled: checked,
                  })),
              })}
              <button
                type="button"
                onClick={() => openTierModal("edit", tierModel)}
                className={`${editButtonClassName} justify-self-start`}
              >
                Edit
              </button>
            </div>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={openAddTierModal}
        disabled={!canAddTier}
        title={
          canAddTier
            ? undefined
            : `You can have up to ${maxMembershipTiers} membership tiers, and all of them are in use. To change a tier, edit it in the list above.`
        }
        className={`${addRowButtonClassName} mt-6`}
      >
        + Add Additional Membership Tiers
      </button>

      <SectionSaveReset {...sectionControls} />
    </section>
  );

  const renderTierBenefits = () => {
    const tierBenefitsGridStyle = getTierBenefitsGridStyle(
      tierMembershipModels.length
    );
    const showAllBenefits =
      memberBenefits.length > 0 &&
      tierMembershipModels.every((tierModel) =>
        memberBenefits.every((benefit) =>
          tierModel.includedBenefitIds.includes(benefit.id)
        )
      );

    return (
      <section className={membershipSetupCardClassName}>
        <div>
          <h2 className={membershipSetupHeaderClassName}>
            Membership Benefits for Tiered Model
          </h2>
          <p className={membershipSetupDescriptionClassName}>
            {applyCmpOrganizationNoun(tierBenefitsDescription, noun)}
          </p>
        </div>

        <div className="mt-5"><MembershipOptionsIntroField
          value={settings.membershipOptionsIntro}
          onChange={(membershipOptionsIntro) => onChange({ ...settings, membershipOptionsIntro })}
        /></div>

        <label className="mt-5 flex items-center gap-[10px] text-[13px] font-semibold text-[#0A0A0A]">
          <Checkbox
            checked={showAllBenefits}
            onChange={(event) => setAllTierBenefits(event.target.checked)}
            sx={inlineChoiceControlSx}
          />
          <span>
            Show all benefits listed below for all membership types. For tier
            memberships you can remove check for any tier.
          </span>
        </label>

        <div className="mt-4 overflow-x-auto bg-white">
          <div
            className="min-w-[1040px]"
            style={{ minWidth: Math.max(1040, 360 + 76 * tierMembershipModels.length + 100 + 12 * (tierMembershipModels.length + 1) + 40) }}
          >
            <div
              className={`${tierBenefitsGridClassName} ${settingsTableHeaderClassName} text-center`}
              style={tierBenefitsGridStyle}
            >
              <div className="text-left">Benefit Description</div>
              {/* TB11 benefitTierHeads: one column per tier, headed by its code. */}
              {tierMembershipModels.map((tierModel, index) => (
                <div
                  key={`${tierModel.id}-header`}
                  className="flex items-center justify-center"
                >
                  {tierModel.code.trim() || `Tier ${index + 1}`}
                </div>
              ))}
              <div />
            </div>
            <div className="space-y-3">
              {memberBenefits.map((benefit) => (
                <div
                  key={`tier-benefit-${benefit.id}`}
                  className={`${tierBenefitsGridClassName} items-center py-3`}
                  style={tierBenefitsGridStyle}
                >
                  <input
                    value={benefit.label}
                    onChange={(event) =>
                      onChange({
                        ...settings,
                        memberBenefits: memberBenefits.map((currentBenefit) =>
                          currentBenefit.id === benefit.id
                            ? {
                                ...currentBenefit,
                                label: event.target.value,
                              }
                            : currentBenefit
                        ),
                      })
                    }
                    placeholder="Enter Membership Benefit Details"
                    className={compactInputClassName}
                  />
                  {tierMembershipModels.map((tierModel) => (
                    <label
                      key={`${tierModel.id}-${benefit.id}`}
                      className="flex h-11 items-center justify-center"
                    >
                      <Checkbox
                        checked={tierModel.includedBenefitIds.includes(
                          benefit.id
                        )}
                        onChange={(event) =>
                          toggleTierBenefit(
                            tierModel.id,
                            benefit.id,
                            event.target.checked
                          )
                        }
                        sx={inlineChoiceControlSx}
                      />
                      <span className="sr-only">
                        Include {benefit.label} in {tierModel.name}
                      </span>
                    </label>
                  ))}
                  <button
                    type="button"
                    onClick={() => setBenefitDeleteCandidateId(benefit.id)}
                    className={`${dangerButtonClassName} justify-self-end`}
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={addBenefit}
          className={`${addRowButtonClassName} mt-4`}
        >
          + Add New Benefit
        </button>

        <SectionSaveReset {...sectionControls} />
      </section>
    );
  };

  const renderBenefits = () => (
    <section className={`${membershipSetupCardClassName} flex flex-col gap-[18px]`}>
      <div>
        <h2 className={membershipSetupHeaderClassName}>
          Membership Benefits for Membership Type (by Size) Model
        </h2>
        <p className={membershipSetupDescriptionClassName}>
          {applyCmpOrganizationNoun(sizeBenefitsDescription, noun)}
        </p>
      </div>

      <MembershipOptionsIntroField
        value={settings.membershipOptionsIntro}
        onChange={(membershipOptionsIntro) => onChange({ ...settings, membershipOptionsIntro })}
      />

      <p className="text-sm font-semibold text-[#0A0A0A]">
        Benefit Description:
      </p>
      <div className="flex flex-col gap-[10px]">
        {memberBenefits.length === 0 ? (
          <p className="rounded-lg bg-gray-50 px-4 py-6 text-center text-sm font-medium text-gray-600">
            No benefits have been added.
          </p>
        ) : (
          memberBenefits.map((benefit) => (
            <div key={benefit.id} className="flex items-center gap-[12px]">
              <input
                value={benefit.label}
                onChange={(event) =>
                  onChange({
                    ...settings,
                    memberBenefits: memberBenefits.map((currentBenefit) =>
                      currentBenefit.id === benefit.id
                        ? { ...currentBenefit, label: event.target.value }
                        : currentBenefit
                    ),
                  })
                }
                placeholder="Enter Membership Benefit Details"
                aria-label="Membership benefit details"
                className={`${compactInputClassName} min-w-0 flex-1`}
              />
              <button
                type="button"
                onClick={() => setBenefitDeleteCandidateId(benefit.id)}
                className={`${dangerButtonClassName} shrink-0`}
              >
                Delete
              </button>
            </div>
          ))
        )}
      </div>

      <button
        type="button"
        onClick={addBenefit}
        className={addRowButtonClassName}
      >
        + Add Benefit
      </button>

      <SectionSaveReset {...sectionControls} />
    </section>
  );

  // Design (11): the add-on section shows in tier and type modes
  // (D:6965-7018, D:7227-7275).
  const renderAddOns = () => (
    <section className={membershipSetupCardClassName}>
      <h2 className={membershipSetupHeaderClassName}>
        Additional Membership Add on Benefits and Fees
      </h2>
      <p className={membershipSetupDescriptionClassName}>
        Create and enable additional membership add on benefits options your
        {" "}{noun.singular} would like to offer to members at time of registration. Each
        add-on can include a name, brief description, and cost, allowing
        prospective members to select and purchase extras based on what they
        include the online registration form, or when your {noun.singular} registers
        a member through the Create Member in your Dashboard.
      </p>

      <div className="mt-5 overflow-x-auto">
        <div className="min-w-[980px]">
          <div
            className={`${settingsTableHeaderClassName} grid ${addOnTableColumnsClassName} items-center gap-4`}
          >
            <span>Code</span>
            <span>Benefit Description</span>
            <span>Fee</span>
            <span>Account Number</span>
            <span>Enable Add-On</span>
            <span>Actions</span>
          </div>

          <div className="space-y-3">
            {addOns.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm font-semibold text-gray-500">
                No additional membership add on benefits and fees have been
                configured.
              </p>
            ) : (
              addOns.map((addOn) => {
                const isPercentage = isPercentageAddOn(addOn);
                return (
                  <div
                    key={addOn.id}
                    className={`grid ${addOnTableColumnsClassName} items-center gap-4 px-3 py-7`}
                  >
                    <span className="text-[13px] text-[#0A0A0A]">
                      {addOn.code.trim() || "-"}
                    </span>
                    <div>
                      <h3 className="text-sm font-semibold text-[#0A0A0A]">
                        {addOn.name}
                      </h3>
                      <p className="text-[13px] text-[#0A0A0A]">
                        {addOn.description}
                      </p>
                    </div>
                    <div className="flex flex-col items-start gap-[6px]">
                      <p className="text-sm font-semibold text-[#0A0A0A]">
                        {isPercentage
                          ? `${formatPercentage(addOn.percentage || "")} of Membership Tier Fee`
                          : addOn.annualAmount}
                      </p>
                      {isPercentage && tierSetupActive ? (
                        <div className="flex flex-col gap-[2px]">
                          {tierMembershipModels.map((tierModel) => (
                            <span
                              key={`${addOn.id}-${tierModel.id}`}
                              className="whitespace-nowrap text-[12px] text-[#0A0A0A]"
                            >
                              {tierModel.name.trim() || tierModel.code}:{" "}
                              {parseDollars(tierModel.annualPrice) > 0
                                ? formatDollars(
                                    getPercentageAddOnAmount(
                                      tierModel.annualPrice,
                                      addOn.percentage || ""
                                    )
                                  )
                                : "No fee set"}
                            </span>
                          ))}
                        </div>
                      ) : null}
                      {!tierSetupActive ? (
                        <button
                          type="button"
                          aria-label={`Edit ${
                            addOn.code || addOn.name || "add-on"
                          } benefit fee`}
                          onClick={() => {
                            setAddOnModalError("");
                            setAddOnModal({ mode: "editFee", addOn });
                          }}
                          className="inline-flex items-center justify-center rounded-[6px] border-0 bg-[#E1A731] px-[12px] py-[5px] text-[11px] font-semibold text-white transition hover:bg-[#C6912A]"
                        >
                          Edit
                        </button>
                      ) : null}
                    </div>
                    <span className="text-sm text-[#0A0A0A]">
                      {addOn.accountNumber.trim() || "-"}
                    </span>
                    {renderSwitch({
                      checked: addOn.enabled,
                      label: `${addOn.name || "Add-on"} enabled`,
                      onChange: (checked) =>
                        applySharedAddOns(
                          addOns.map((currentAddOn) =>
                            currentAddOn.id === addOn.id
                              ? { ...currentAddOn, enabled: checked }
                              : currentAddOn
                          )
                        ),
                    })}
                    <div className="grid justify-start gap-[8px]">
                      <button
                        type="button"
                        aria-label={`Edit ${addOn.name || "add-on"}`}
                        onClick={() => {
                          setAddOnModalError("");
                          setAddOnModal({
                            mode: "edit",
                            addOn: {
                              ...addOn,
                              feeType: addOn.feeType || "fixed",
                              percentage: addOn.percentage || "",
                            },
                          });
                        }}
                        className={editButtonClassName}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => setAddOnDeleteCandidateId(addOn.id)}
                        className={dangerButtonClassName}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => {
          setAddOnModalError("");
          setAddOnModal({ mode: "add", addOn: createNewAddOn() });
        }}
        className={`${addRowButtonClassName} mt-5`}
      >
        + Add New Benefits and Fees
      </button>

      <SectionSaveReset {...sectionControls} />
    </section>
  );

  // Design (11) prorated fees (D:7020-7073 tiers, D:7276-7328 types; maths
  // D:14657-14737): one block per enabled membership, 12 Membership Period
  // cells from the fiscal start month, a period shared by every block and a
  // custom amount shared as a ratio of each block's 12 month fee.
  const renderProratedPricing = () => {
    const setupForProration = prorationSetup || {
      fiscalYearStartMonth: "January",
      membershipRenewalFixedDateEnabled: false,
      membershipRenewalFixedDateMonth: "January",
      multipleBillingDates: undefined,
    };
    const multiBillingOffered = !!prorationSetup?.multipleBillingDatesAvailable;

    if (!isProratedPricingActive(setupForProration, settings)) {
      return (
        <section className={`${membershipSetupCardClassName} flex flex-col gap-[6px]`}>
          <h2 className={membershipSetupHeaderClassName}>
            Prorated Company Membership Fees Are Not Enabled
          </h2>
          <p className="text-[14px] text-[#0A0A0A]">
            {multiBillingOffered
              ? "To generate prorated fees for new members, enable Membership Renewal Fix Date or Membership Renewal Multi-Billing Dates (with dates selected) and Prorated Company Membership Fees in Chamber Global Settings, then click Save Changes."
              : "To generate prorated fees for new members, enable Membership Renewal Fix Date (with a date selected) and Prorated Company Membership Fees in Chamber Global Settings, then click Save Changes."}
          </p>
        </section>
      );
    }

    const cells = getProrationCells(setupForProration);
    const periodMonths = settings.proratedPeriodMonths || {};
    const getCellMonths = (cell: ProrationCell) =>
      periodMonths[cell.monthLabel] ?? cell.defaultMonths;

    const blockName = (name: string) =>
      /membership$/i.test(name.trim()) ? name.trim() : `${name.trim()} Membership`;
    const companyBlocks =
      !tierSetupActive &&
      companyMembershipModel?.enabled &&
      companyMembershipModel.publicMembershipApplicationEnabled
        ? companyMembershipModel.ranges.map((range) => ({
            key: `${companyMembershipModel.id}-${range.id}`,
            name: `Company Membership (${
              range.to.trim()
                ? `${range.from || "1"} to ${range.to.trim()}`
                : `${range.from || "1"}+`
            } employees)`,
            fee: range.annualPrice,
          }))
        : [];
    const blocks = tierSetupActive
      ? enabledTierMembershipModels.map((model) => ({
          key: model.id,
          name: blockName(model.name),
          fee: model.annualPrice,
        }))
      : [
          ...companyBlocks,
          ...enabledFixedMembershipModels.map((model) => ({
            key: model.id,
            name: model.name,
            fee: model.annualPrice,
          })),
        ];

    const getDefaultAmount = (fee: string, cell: ProrationCell) =>
      formatDollars(getProratedAmountInDollars(parseDollars(fee), getCellMonths(cell)));
    const getAmount = (blockKey: string, fee: string, cell: ProrationCell) =>
      proratedPrices[blockKey]?.[cell.monthLabel] ?? getDefaultAmount(fee, cell);

    const setPeriodEnd = (cell: ProrationCell, endLabel: string) => {
      const months = getProrationMonthsForEndLabel(cell, endLabel);
      if (!months) return;
      const nextPeriods = { ...periodMonths };
      if (months === cell.defaultMonths) delete nextPeriods[cell.monthLabel];
      else nextPeriods[cell.monthLabel] = months;
      // A new period resets that month's amounts to fee x months / 12.
      const nextPrices = Object.fromEntries(
        Object.entries(proratedPrices).map(([key, months]) => {
          const { [cell.monthLabel]: _removed, ...rest } = months || {};
          return [key, rest];
        })
      );
      updateProratedPrices(nextPrices, nextPeriods);
    };

    const setAmount = (blockKey: string, cell: ProrationCell, value: string) =>
      updateProratedPrices({
        ...proratedPrices,
        [blockKey]: { ...proratedPrices[blockKey], [cell.monthLabel]: value },
      });

    // A custom amount is shared as a ratio of the 12 month fee (D:14725).
    const shareAmount = (
      blockKey: string,
      fee: string,
      cell: ProrationCell,
      value: string
    ) => {
      const formatted = formatMoneyValueForDisplay(value);
      const blockFee = parseDollars(fee);
      // Back to the generated amount: every block follows the period again.
      if (formatted === getDefaultAmount(fee, cell)) {
        updateProratedPrices(
          Object.fromEntries(
            Object.entries(proratedPrices).map(([key, prices]) => {
              const { [cell.monthLabel]: _removed, ...rest } = prices || {};
              return [key, rest];
            })
          )
        );
        return;
      }
      const nextPrices = {
        ...proratedPrices,
        [blockKey]: { ...proratedPrices[blockKey], [cell.monthLabel]: formatted },
      };
      if (formatted && blockFee > 0) {
        const ratio = parseDollars(formatted) / blockFee;
        blocks.forEach((block) => {
          const otherFee = parseDollars(block.fee);
          if (block.key === blockKey || !(otherFee > 0)) return;
          nextPrices[block.key] = {
            ...nextPrices[block.key],
            [cell.monthLabel]: formatDollars(Math.round(otherFee * ratio * 100) / 100),
          };
        });
      }
      updateProratedPrices(nextPrices);
    };

    return (
      <section className={`${membershipSetupCardClassName} flex flex-col gap-[20px]`}>
        <div>
          <h2 className={membershipSetupHeaderClassName}>
            {tierSetupActive
              ? `Prorated Company Membership Tiers Fees has Been Enabled for Your ${noun.Singular} New Members Only`
              : `Prorated Company Membership Type Fees has Been Enabled for Your ${noun.Singular} New Members Only`}
          </h2>
          {tierSetupActive ? (
            <div className="mt-[4px] flex flex-col gap-[8px] text-[14px] text-[#0A0A0A]">
              <span>
                Monthly prorated Company Membership fees have been generated
                below for new members. You can keep the prorated amounts or
                enter a custom fee for any month.
              </span>
              <span>
                You can also extend the membership period for memberships
                purchased within the final three months of the current period,
                allowing the remaining months to be added to the new member’s
                next membership term.
              </span>
            </div>
          ) : (
            <p className={membershipSetupDescriptionClassName}>
              Monthly prorated Company Membership fees have been generated
              below. If you do not want the fee to be prorated you can set to a
              custom amount for each month, simply enter amount.
            </p>
          )}
        </div>

        {blocks.length === 0 ? (
          <p className="text-[14px] text-[#727272]">
            No memberships are enabled. Enable a membership above to generate
            its prorated fees.
          </p>
        ) : (
          blocks.map((block) => (
            <div
              key={`prorated-${block.key}`}
              className="flex flex-col gap-[16px] rounded-[10px] border border-[#ECECEC] p-[20px]"
            >
              <h3 className="text-[15px] font-semibold text-[#0A0A0A]">
                Prorated {block.name} Fees
              </h3>
              <label className="flex max-w-[240px] flex-col gap-[6px]">
                <span className="text-[13px] font-semibold text-[#0A0A0A]">
                  {tierSetupActive ? "12 Months Membership Fee" : "Annual Membership Fee"}
                </span>
                <input
                  value={
                    parseDollars(block.fee) > 0
                      ? block.fee
                      : tierSetupActive
                      ? "Call for Pricing"
                      : block.fee || "$0.00"
                  }
                  readOnly
                  className="w-full rounded-[8px] border border-[#9E9E9E] bg-[#F7F7F8] px-[12px] py-[9px] text-[14px] text-[#0A0A0A] outline-none"
                />
              </label>
              <div>
                <h4 className="text-[14px] font-semibold text-[#0A0A0A]">
                  Prorated signup pricing: {block.name}
                </h4>
                <p className="mt-[3px] text-[13px] text-[#0A0A0A]">
                  {applyCmpOrganizationNoun(proratedMembershipDescription, noun)}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-[12px] md:grid-cols-4 min-[1401px]:grid-cols-6">
                {cells.map((cell) => {
                  const months = getCellMonths(cell);
                  return (
                    <div
                      key={`${block.key}-${cell.monthLabel}`}
                      className="flex min-w-0 flex-col gap-[5px]"
                    >
                      <div className="flex min-w-0 flex-col gap-[3px] text-[12px] text-[#0A0A0A]">
                        <span className="font-semibold">Membership Period</span>
                        <span>{formatProrationMonths(months)}</span>
                        <div className="flex min-w-0 flex-wrap items-center gap-[4px]">
                          <span className="whitespace-nowrap">{cell.startLabel} to</span>
                          <MonthYearPicker
                            value={getProrationEndLabel(cell, months)}
                            minValue={cell.startLabel}
                            placeholder="Select Month"
                            ariaLabel={`${block.name} ${cell.startLabel} membership period end`}
                            onChange={(endLabel) => setPeriodEnd(cell, endLabel)}
                          />
                        </div>
                      </div>
                      <input
                        inputMode="decimal"
                        aria-label={`${block.name} prorated price for ${cell.startLabel}`}
                        value={getAmount(block.key, block.fee, cell)}
                        onChange={(event) =>
                          setAmount(block.key, cell, sanitizeMoneyInput(event.target.value))
                        }
                        onBlur={(event) =>
                          shareAmount(block.key, block.fee, cell, event.target.value)
                        }
                        className="w-full rounded-[8px] border border-[#9E9E9E] px-[10px] py-[9px] text-[13px] text-[#0A0A0A] outline-none focus:border-cpaGold"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}

        <SectionSaveReset {...sectionControls} />
      </section>
    );
  };

  const closeMembershipTypeModal = () => {
    setMembershipTypeModalError("");
    setMembershipTypeModal(null);
  };

  const updateModalModel = (patch: Partial<MembershipModel>) =>
    setMembershipTypeModal((current) =>
      current ? { ...current, model: { ...current.model, ...patch } } : current
    );

  const updateAddOnModal = (patch: Partial<MembershipAdditionalCost>) =>
    setAddOnModal((current) =>
      current ? { ...current, addOn: { ...current.addOn, ...patch } } : current
    );

  const renderMembershipTypeModalFooter = () => (
    <>
      {membershipTypeModalError && (
        <p role="alert" className="text-center text-sm font-semibold text-red-600">
          {membershipTypeModalError}
        </p>
      )}
      <div className="mt-[4px] flex justify-center">
        <button
          type="button"
          onClick={saveMembershipTypeModal}
          className={modalSaveButtonClassName}
        >
          Save
        </button>
      </div>
    </>
  );

  return (
    <div className="space-y-6">
      {renderMembershipGlobalSettings()}
      {renderMembershipModelSelector()}
      {tierSetupActive ? (
        <>
          {renderTierSettings()}
          {renderTierBenefits()}
          {renderAddOns()}
        </>
      ) : (
        <>
          {renderSizeBasedSettings()}
          {renderBenefits()}
          {renderAddOns()}
        </>
      )}
      {renderProratedPricing()}

      {membershipTypeModal && isTierModel(membershipTypeModal.model) && (
        <MembershipModal
          title={
            membershipTypeModal.mode === "edit"
              ? "Edit Membership Tiers Name and Description"
              : "Add Membership Tier Name and Description"
          }
          closeLabel="Close membership tier modal"
          onClose={closeMembershipTypeModal}
        >
          {/* Design (11) tier modal (D:8437-8515): Code | Name side by side. */}
          <div className="grid grid-cols-1 gap-x-[20px] gap-y-[20px] sm:grid-cols-2">
            <MembershipModalField label="Code" required labelClassName={tierModalLabelClassName}>
              <input
                value={membershipTypeModal.model.code}
                placeholder="Enter Membership Tier Code"
                maxLength={20}
                onChange={(event) => updateModalModel({ code: event.target.value })}
                className={modalInputClassName}
              />
            </MembershipModalField>
            <MembershipModalField
              label="Membership Tier Name"
              required
              labelClassName={tierModalLabelClassName}
              counter={`${membershipTypeModal.model.name.length}/100 Characters`}
            >
              <input
                value={membershipTypeModal.model.name}
                placeholder="Enter Membership Tier Name"
                maxLength={100}
                onChange={(event) => updateModalModel({ name: event.target.value })}
                className={modalInputClassName}
              />
            </MembershipModalField>
          </div>
          <MembershipModalField
            label="Membership Tier Description and Criteria"
            required
            labelClassName={tierModalLabelClassName}
            counter={`${membershipTypeModal.model.description.length}/200 Characters`}
          >
            <textarea
              value={membershipTypeModal.model.description}
              placeholder="Enter Membership Tier Description"
              maxLength={200}
              onChange={(event) =>
                updateModalModel({ description: event.target.value })
              }
              className={modalTextareaClassName}
            />
          </MembershipModalField>
          <div className="flex flex-col gap-[8px]">
            <span className={tierModalLabelClassName}>Membership Eligibility</span>
            <span className="text-[14px]">
              Enter the range of the number of employees this membership is
              eligible to.
            </span>
            <div className="grid grid-cols-2 gap-x-[20px] gap-y-[6px]">
              <span className="text-[13px] font-semibold text-[#0A0A0A]">Number of Employees From</span>
              <span className="text-[13px] font-semibold text-[#0A0A0A]">Number of Employees To</span>
              <WholeNumberInput
                value={membershipTypeModal.model.eligibilityEmployeesFrom || ""}
                placeholder="e.g. 1"
                ariaLabel="Number of Employees From"
                onChange={(value) =>
                  updateModalModel({ eligibilityEmployeesFrom: value })
                }
                className={modalInputClassName}
              />
              <WholeNumberInput
                value={membershipTypeModal.model.eligibilityEmployeesTo || ""}
                placeholder="e.g. 25"
                ariaLabel="Number of Employees To"
                onChange={(value) =>
                  updateModalModel({ eligibilityEmployeesTo: value })
                }
                className={modalInputClassName}
              />
            </div>
          </div>
          <div className="flex flex-col gap-[8px]">
            <span className={tierModalLabelClassName}>Membership Tier Icon</span>
            <span className="text-[14px]">
              Upload an icon that best represents this membership tier. It
              appears alongside the tier name.
            </span>
            {membershipTypeModal.model.iconUrl ? (
              <div className="flex items-center gap-[16px] rounded-[12px] border border-[#9E9E9E] bg-white px-[16px] py-[14px]">
                <div className="box-border flex h-[56px] w-[56px] shrink-0 items-center justify-center overflow-hidden rounded-[10px] border border-[#ECECEC] bg-[#F7F7F8] p-[6px]">
                  <img
                    src={membershipTypeModal.model.iconUrl}
                    alt="Membership tier icon"
                    className="h-full w-full object-contain"
                  />
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
                  <span
                    title={tierIconFile?.name}
                    className="truncate text-[14px] font-semibold text-[#0A0A0A]"
                  >
                    {tierIconFile?.name || "Membership tier icon"}
                  </span>
                  <span className="text-[12px] text-[#727272]">
                    {[tierIconFile ? formatTierIconSize(tierIconFile.size) : "", "Uploaded"]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-[8px]">
                  <label className="inline-flex cursor-pointer items-center rounded-[8px] border border-[#E1A731] bg-white px-[16px] py-[9px] text-[14px] font-bold text-[#0A0A0A]">
                    <input
                      type="file"
                      accept={tierIconMimeTypes.join(",")}
                      className="sr-only"
                      onChange={(event) => {
                        void selectTierIcon(event.target.files?.[0]);
                        event.target.value = "";
                      }}
                    />
                    <span>Replace</span>
                  </label>
                  <button
                    type="button"
                    onClick={removeTierIcon}
                    title="Remove icon"
                    aria-label="Remove icon"
                    className="flex h-[38px] w-[38px] items-center justify-center rounded-[8px] border border-[#ECECEC] bg-white hover:border-[#E24C4C] hover:bg-[#FDECEC]"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="#E24C4C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-[16px] w-[16px]" aria-hidden="true">
                      <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />
                    </svg>
                  </button>
                </div>
              </div>
            ) : (
              <label
                onDragOver={(event) => {
                  event.preventDefault();
                  setTierIconDragging(true);
                }}
                onDragLeave={() => setTierIconDragging(false)}
                onDrop={(event) => {
                  event.preventDefault();
                  setTierIconDragging(false);
                  void selectTierIcon(event.dataTransfer.files?.[0]);
                }}
                className={`flex cursor-pointer items-center gap-[16px] rounded-[12px] border-[1.5px] border-dashed px-[20px] py-[18px] transition-colors ${
                  tierIconDragging
                    ? "border-[#E1A731] bg-[#FBF1DC]"
                    : "border-[#9E9E9E] bg-[#F7F7F8]"
                }`}
              >
                <input
                  type="file"
                  accept={tierIconMimeTypes.join(",")}
                  aria-label="Upload membership tier icon"
                  className="sr-only"
                  onChange={(event) => {
                    void selectTierIcon(event.target.files?.[0]);
                    event.target.value = "";
                  }}
                />
                <div className="flex h-[48px] w-[48px] shrink-0 items-center justify-center rounded-[10px] bg-[#FBF1DC]">
                  <svg viewBox="0 0 24 24" fill="none" stroke="#E1A731" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-[22px] w-[22px]" aria-hidden="true">
                    <path d="M12 16V4M7 9l5-5 5 5" />
                    <path d="M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />
                  </svg>
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-[4px]">
                  <span className="text-[14px] font-semibold text-[#0A0A0A]">
                    Drag and drop an icon here, or{" "}
                    <span className="text-[#B7831B] underline underline-offset-2">browse files</span>
                  </span>
                  <span className="text-[12px] text-[#727272]">
                    PNG, JPG or SVG · square, at least 120 × 120 px · up to 2 MB
                  </span>
                </div>
              </label>
            )}
            {tierIconError ? (
              <span role="alert" className="text-[12px] text-[#E24C4C]">
                {tierIconError}
              </span>
            ) : null}
          </div>
          <MembershipModalField
            label="Membership Tier Price"
            required
            labelClassName="text-[14px] font-bold tracking-[.02em]"
            helper={
              <>
                If no price is entered, the box will display “<strong>Call for Pricing</strong>”.
              </>
            }
          >
            <MoneyInput
              value={membershipTypeModal.model.annualPrice}
              placeholder="$0.00"
              ariaLabel="Membership tier price"
              onChange={(value) =>
                updateModalModel({
                  annualPrice: value,
                  monthlyPrice: getDerivedMonthlyMembershipPrice(value),
                })
              }
              className={modalInputClassName}
            />
          </MembershipModalField>
          <MembershipModalField
            label="Member Target"
            helper="Set the target number of members you aim to have for this membership tier. This goal is used for tracking membership growth and reporting progress."
          >
            <WholeNumberInput
              value={
                membershipTypeModal.model.memberTarget
                  ? String(membershipTypeModal.model.memberTarget)
                  : ""
              }
              placeholder="00"
              ariaLabel="Membership tier member target"
              onChange={(value) =>
                updateModalModel({ memberTarget: Number(value) || 0 })
              }
              className={modalInputClassName}
            />
          </MembershipModalField>
          {renderMembershipTypeModalFooter()}
        </MembershipModal>
      )}

      {membershipTypeModal && !isTierModel(membershipTypeModal.model) && (
        <MembershipModal
          title="Membership Type and Description"
          closeLabel="Close membership type modal"
          onClose={closeMembershipTypeModal}
        >
          <MembershipModalField label="CODE" required>
            <input
              value={membershipTypeModal.model.code}
              placeholder="Enter Membership Type Code"
              maxLength={20}
              onChange={(event) => {
                setMembershipTypeModalError("");
                updateModalModel({ code: event.target.value });
              }}
              className={modalInputClassName}
            />
          </MembershipModalField>
          <MembershipModalField
            label="MEMBERSHIP TYPE NAME"
            required
            counter={`${membershipTypeModal.model.name.length}/100 Characters`}
          >
            <input
              value={membershipTypeModal.model.name}
              maxLength={100}
              placeholder="Enter Membership Type Name"
              onChange={(event) => updateModalModel({ name: event.target.value })}
              className={modalInputClassName}
            />
          </MembershipModalField>
          <MembershipModalField
            label="MEMBERSHIP TYPE DESCRIPTION"
            required
            counter={`${membershipTypeModal.model.description.length}/200 Characters`}
          >
            <textarea
              value={membershipTypeModal.model.description}
              maxLength={200}
              placeholder="Enter Membership Model Description"
              onChange={(event) =>
                updateModalModel({ description: event.target.value })
              }
              className={modalTextareaClassName}
            />
          </MembershipModalField>
          <MembershipModalField
            label="MEMBER TARGET"
            helper={
              <span className="text-[13px]">
                Set the target number of members you aim to have for this
                membership type. This goal is used for tracking membership
                growth and reporting progress.
              </span>
            }
          >
            <WholeNumberInput
              value={
                membershipTypeModal.model.memberTarget
                  ? String(membershipTypeModal.model.memberTarget)
                  : ""
              }
              placeholder="00"
              ariaLabel="Membership type member target"
              onChange={(value) =>
                updateModalModel({ memberTarget: Number(value) || 0 })
              }
              className={modalInputClassName}
            />
          </MembershipModalField>
          <MembershipModalField
            label="MEMBERSHIP TYPE FEE"
            required
            helper={
              <span className="text-[13px]">
                If no price is entered, the box will display “<strong>Call for Pricing</strong>”.
              </span>
            }
          >
            <MoneyInput
              value={membershipTypeModal.model.annualPrice}
              placeholder="$0.00"
              ariaLabel="Membership type fee"
              onChange={(value) =>
                updateModalModel({
                  annualPrice: value,
                  monthlyPrice: getDerivedMonthlyMembershipPrice(value),
                })
              }
              className={modalInputClassName}
            />
          </MembershipModalField>
          {renderMembershipTypeModalFooter()}
        </MembershipModal>
      )}

      <ConfirmDialog
        open={Boolean(benefitDeleteCandidateId || addOnDeleteCandidateId)}
        variant="destructive"
        ariaLabel="Delete benefit"
        message="Are you sure you want to delete the benefit?"
        confirmLabel="Delete"
        onConfirm={() => {
          if (benefitDeleteCandidateId) deleteBenefit(benefitDeleteCandidateId);
          if (addOnDeleteCandidateId) deleteAddOn(addOnDeleteCandidateId);
        }}
        onClose={() => {
          setBenefitDeleteCandidateId(null);
          setAddOnDeleteCandidateId(null);
        }}
      />

      {addOnModal && addOnModal.mode !== "editFee" && (
        <MembershipModal
          title={
            addOnModal.mode === "edit"
              ? "Edit Benefits and Fees"
              : "Add New Benefits and Fees"
          }
          titleClassName="text-[18px] font-semibold"
          description={
            addOnModal.mode === "edit"
              ? "Update the details of this membership add-on benefit and fee."
              : "Fill in the form below to add a new membership add-on benefit and fee."
          }
          closeLabel="Close benefits and fees modal"
          onClose={closeAddOnModal}
        >
          {/* Design (11) add-on modal (D:8376-8425). */}
          <MembershipModalField label="CODE" optional>
            <input
              value={addOnModal.addOn.code}
              placeholder="Enter Code"
              maxLength={10}
              onChange={(event) => updateAddOnModal({ code: event.target.value })}
              className={modalInputClassName}
            />
          </MembershipModalField>
          <MembershipModalField
            label="BENEFIT NAME"
            required
            counter={`${addOnModal.addOn.name.length}/100 characters`}
          >
            <input
              value={addOnModal.addOn.name}
              maxLength={100}
              placeholder="Enter Additional Benefit Name"
              onChange={(event) => updateAddOnModal({ name: event.target.value })}
              className={modalInputClassName}
            />
          </MembershipModalField>
          <MembershipModalField
            label="BENEFIT DESCRIPTION"
            counter={`${addOnModal.addOn.description.length}/250 characters`}
          >
            <textarea
              value={addOnModal.addOn.description}
              maxLength={250}
              placeholder="Enter Additional Benefit Description"
              onChange={(event) =>
                updateAddOnModal({ description: event.target.value })
              }
              className={`${modalTextareaClassName} !h-[80px]`}
            />
          </MembershipModalField>
          <div className="flex flex-col gap-[8px]">
            <span className="text-[13px] font-bold tracking-[.02em]">
              BENEFIT FEE<span className="text-[#E24C4C]">*</span>
            </span>
            <div className="flex flex-wrap gap-[10px]" role="group" aria-label="Benefit fee type">
              {(
                [
                  ["fixed", "Fixed Amount"],
                  ["percentage", "Percentage of Membership Tier"],
                ] as const
              ).map(([feeType, label]) => {
                const selected = (addOnModal.addOn.feeType || "fixed") === feeType;
                return (
                  <button
                    key={feeType}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => updateAddOnModal({ feeType })}
                    className={`rounded-[8px] border px-[16px] py-[10px] text-[14px] font-semibold ${
                      selected
                        ? "border-[#E1A731] bg-[#E1A731] text-white"
                        : "border-[#9E9E9E] bg-white text-[#0A0A0A]"
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            {isPercentageAddOn(addOnModal.addOn) ? (
              <>
                <div className="flex max-w-[200px] items-center gap-[8px]">
                  <DecimalNumberInput
                    value={addOnModal.addOn.percentage || ""}
                    placeholder="0"
                    ariaLabel="Benefit fee percentage"
                    onChange={(value) => updateAddOnModal({ percentage: value })}
                    className={modalInputClassName}
                  />
                  <span className="text-[14px] font-semibold">%</span>
                </div>
                <span className="text-[13px] text-[#727272]">
                  Charged annually on renewal, calculated from the fee of the
                  membership tier the member selects.
                </span>
              </>
            ) : (
              <MoneyInput
                value={addOnModal.addOn.annualAmount}
                placeholder="$0.00"
                ariaLabel="Benefit fee"
                onChange={(value) =>
                  updateAddOnModal({ annualAmount: value, monthlyAmount: value })
                }
                className={modalInputClassName}
              />
            )}
          </div>
          {/* Claude Design (21) CMP Dashboard.dc.html:10362-10415 has no tax
              control (Moeen, IF11 N8). A saved taxExempt value stays on the
              add-on and keeps applying (settingsMappers round trip). */}
          <MembershipModalField label="ACCOUNT NUMBER" optional>
            {/* Claude Design (13) CMP Dashboard 8435: SearchableSelect. */}
            <SearchableSelectField
              id="membership-add-on-account-number"
              value={addOnModal.addOn.accountNumber}
              onChange={(accountNumber) => updateAddOnModal({ accountNumber })}
              placeholder="Select Account Number"
              searchPlaceholder="Search Account Number"
              options={accountNumberOptions.map((accountNumber) => ({
                value: accountNumber,
                label: accountNumber,
              }))}
            />
          </MembershipModalField>
          {addOnModalError ? (
            <p role="alert" className="text-right text-[13px] text-[#F21B1B]">
              {addOnModalError}
            </p>
          ) : null}
          <div className="mt-[4px] flex justify-end">
            <button
              type="button"
              onClick={saveAddOnModal}
              className={modalSaveButtonClassName}
            >
              Save
            </button>
          </div>
        </MembershipModal>
      )}

      {addOnModal?.mode === "editFee" && (
        <MembershipModal
          title={
            isPercentageAddOn(addOnModal.addOn)
              ? "Enter percentage to be used"
              : "Edit Benefit Fee"
          }
          titleClassName="text-[17px] font-bold"
          panelClassName="w-[370px] items-center px-[30px] py-[28px]"
          closeLabel="Close edit benefit fee modal"
          onClose={closeAddOnModal}
        >
          {isPercentageAddOn(addOnModal.addOn) ? (
            <div className="flex w-full items-center gap-[8px]">
              <DecimalNumberInput
                value={addOnModal.addOn.percentage || ""}
                placeholder="0"
                ariaLabel="Benefit fee percentage"
                onChange={(value) => updateAddOnModal({ percentage: value })}
                className={modalInputClassName}
              />
              <span className="text-[14px] font-semibold">%</span>
            </div>
          ) : (
            <MoneyInput
              value={addOnModal.addOn.annualAmount}
              placeholder="$0.00"
              ariaLabel="Benefit fee"
              onChange={(value) => updateAddOnModal({ annualAmount: value })}
              className={modalInputClassName}
            />
          )}
          {addOnModalError ? (
            <p role="alert" className="text-center text-[13px] text-[#F21B1B]">
              {addOnModalError}
            </p>
          ) : null}
          <button
            type="button"
            onClick={saveAddOnModal}
            className={`${modalSaveButtonClassName} !px-[40px] !py-[12px]`}
          >
            Save
          </button>
        </MembershipModal>
      )}

      <ActionSuccessDialog
        message={addOnSuccess?.message || null}
        highlights={addOnSuccess ? [addOnSuccess.name] : []}
        onClose={() => setAddOnSuccess(null)}
      />
    </div>
  );
};

export default MembershipTab;
